import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { drillingService } from '../services/drillingService';
import { geometryService } from '../services/geometryService';
import { rasterAssetsService } from '../services/rasterAssetsService';
import { buildDemTerrainMesh, computeTerrainRoiBounds, computeAdaptiveGridSegments } from '../utils/rasterTerrain';
import { buildDistanceTable, interpolateInterval, interpolateIntervalWithOffset } from '../utils/traceInterpolation';
import {
  DOWNHOLE_SOURCES, SOURCE_IDS, buildLegendForSource,
  getIntervalColor, setIntervalThreeColor, getRadiusMultiplier,
} from '../utils/downholeRegistry';
import { assignChannels } from '../utils/channelAssignment';
import { buildLayerVisualBundle } from '../utils/geometryVisualRegistry';
import { assignLayerChannels } from '../utils/geometryChannelAssignment';
import { buildGeometrySegments, buildTraceLines, syntheticTraceFromLayers } from '../utils/buildGeometryScene';
import { SCENE_FIXED_ORIGIN as GEOM_SCENE_ORIGIN, collarToWorld, findSelectedCollar } from '../utils/geometryCoords';
import {
  CONFIGURED_DEM_ASSET_ID,
  CONFIGURED_DEM_NATIVE_EPSG,
  CONFIGURED_ORTHO_ASSET_ID,
  ORTHO_USE_SERVER_WARP_WINDOW,
  ORTHO_USE_XYZ_TILES,
  ORTHO_XYZ_TMS_Y,
  TERRAIN_DEBUG_PROBE,
  USE_DRILLZ_GEOMETRY,
} from '../config/terrainAssets';
import SpatialFilterControls from './SpatialFilterControls';
import './DrillHole3DViewer.css';

/**
 * 3D scene convention (must match rasterTerrain buildDemTerrainMesh):
 * - API / PostGIS: x=easting, y=northing, z=elevation (relative to selected collar from nearby_traces).
 * - Three.js traces: Vector3(api.x, api.z, api.y) → X=east, Y=up, Z=north.
 *   Scene origin = selected collar (always); nearby holes placed via collar-relative offsets from API.
 *   DEM ROI expands to cover all collars; grid resolution scales with ROI span.
 * - Terrain vertical tweak: mesh.position.y (viewer “up” / GIS elevation) via slider; default 0 m.
 * - GLTF/DXF surfaces: vertices are often **local** (near origin) while dxf_gltf_service.assets bbox is **world** UTM.
 *   Placement: root.position = apiBboxCenter_viewer − geometryCentroid_local − collar_viewer (falls back to −collar if no bbox).
 * - AxesHelper: red=X east, green=Y up, blue=Z north.
 * - Compass N/E/S/W snaps: camera sits on the +Z/+X/−Z/−X side of the target and looks at the collar (e.g. N = on north side, view axis −Z). Plan view: camera +Y, up (0,0,−1). Matches legacy behavior vs QGIS north-up exports.
 * - Terrain needs collar_world from getNearbyTraces; DEM native CRS is CONFIGURED_DEM_NATIVE_EPSG (must match trace geom SRID).
 */

function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

/**
 * Fixed scene origin in native CRS (EPSG matching CONFIGURED_DEM_NATIVE_EPSG).
 * Every 3D object is placed at (absoluteE − 384700, absoluteZ, absoluteN − 6944700).
 * Elevation origin is 0 so viewer Y = absolute elevation ASL — all objects share
 * the same vertical datum and nothing shifts when the selected hole changes.
 */
const SCENE_FIXED_ORIGIN = GEOM_SCENE_ORIGIN;

/** Default COG ROI half-width around collar (m); grows when the camera zooms out. */
const DEFAULT_TERRAIN_MARGIN_M = 280;

/** Default shift along viewer +Y (elevation / GIS Z) for DEM mesh vs trace collar-relative heights. */
const DEFAULT_TERRAIN_VERTICAL_OFFSET_M = 0;
const TERRAIN_VERTICAL_OFFSET_MIN_M = -10;
const TERRAIN_VERTICAL_OFFSET_MAX_M = 10;

/** Map orbit distance to ROI margin (m): wider view samples more terrain tiles. */
function terrainMarginFromCameraDistance(distance) {
  const minM = 220;
  const maxM = 1400;
  const dNear = 120;
  const dFar = 900;
  const t = Math.max(0, Math.min(1, (distance - dNear) / Math.max(1, dFar - dNear)));
  return Math.round(minM + t * (maxM - minM));
}

const TUBE_RADIUS = 3;
const SATELLITE_OFFSET = TUBE_RADIUS * 2.8;
const MAX_LABELS = 40;
const SELECTED_COLOR = 0xffcc00;
const NEARBY_COLOR = 0x6b7280;

/** @typedef {'solid' | 'wireframe'} TerrainStyle */
const TERRAIN_STYLE = {
  SOLID: 'solid',
  WIREFRAME: 'wireframe',
};

/** Ortho-draped terrain only: underside fill + live shader debug via uniforms. */
const TERRAIN_UNDERSIDE_MODE = {
  ON: 'on',
  OFF: 'off',
  DEBUG_NORMALS: 'debug_normals',
  DEBUG_UNDERSIDE: 'debug_underside',
};

function getInitialTerrainUndersideMode() {
  if (TERRAIN_DEBUG_PROBE === 1) return TERRAIN_UNDERSIDE_MODE.DEBUG_NORMALS;
  if (TERRAIN_DEBUG_PROBE === 2) return TERRAIN_UNDERSIDE_MODE.DEBUG_UNDERSIDE;
  return TERRAIN_UNDERSIDE_MODE.ON;
}

/** @param {string} mode One of TERRAIN_UNDERSIDE_MODE */
function applyTerrainUndersideUniforms(mesh, mode) {
  const m = mesh?.material;
  if (!mesh?.userData?.terrainUndersideFill || !m?.userData?.terrainUndersideEnabled || !m?.userData?.terrainDebugProbe) {
    return;
  }
  const en = m.userData.terrainUndersideEnabled;
  const db = m.userData.terrainDebugProbe;
  if (mode === TERRAIN_UNDERSIDE_MODE.OFF) {
    en.value = 0;
    db.value = 0;
  } else if (mode === TERRAIN_UNDERSIDE_MODE.ON) {
    en.value = 1;
    db.value = 0;
  } else if (mode === TERRAIN_UNDERSIDE_MODE.DEBUG_NORMALS) {
    en.value = 1;
    db.value = 1;
  } else if (mode === TERRAIN_UNDERSIDE_MODE.DEBUG_UNDERSIDE) {
    en.value = 1;
    db.value = 2;
  }
}

function applyTerrainMaterialStyle(mesh, style) {
  if (!mesh?.material) return;
  const m = mesh.material;
  const hasMap = !!m.map;
  const undersideOrtho = Boolean(mesh.userData?.terrainUndersideFill);
  m.wireframe = style === TERRAIN_STYLE.WIREFRAME;
  m.transparent = undersideOrtho || style === TERRAIN_STYLE.SOLID;
  m.opacity = style === TERRAIN_STYLE.SOLID ? 0.78 : 1;
  // Ortho underside uses alpha 1 in shader; depth write avoids see-through ordering against the scene.
  m.depthWrite = undersideOrtho ? true : style !== TERRAIN_STYLE.SOLID;
  if (style === TERRAIN_STYLE.WIREFRAME) {
    m.color.setHex(hasMap ? 0xffffff : 0x7a8f7a);
  } else {
    m.color.setHex(hasMap ? 0xffffff : 0x3d4f3f);
  }
  if (m.userData?.terrainWireframe) {
    m.userData.terrainWireframe.value = m.wireframe ? 1 : 0;
  }
  m.needsUpdate = true;
}

/** Runtime orientation overrides: mesh.scale for DEM, texture.repeat/offset for ortho. */
function applyTerrainDebugFlips(mesh, flips) {
  if (!mesh) return;
  mesh.scale.x = flips.demEW ? -1 : 1;
  mesh.scale.z = flips.demNS ? -1 : 1;
  const tex = mesh.material?.map;
  if (tex) {
    tex.repeat.x = flips.orthoU ? -1 : 1;
    tex.offset.x = flips.orthoU ? 1 : 0;
    tex.repeat.y = flips.orthoV ? -1 : 1;
    tex.offset.y = flips.orthoV ? 1 : 0;
  }
}

/** No DEM/ortho mirror debug (Orient UI removed). */
const NO_TERRAIN_DEBUG_FLIPS = { demNS: false, demEW: false, orthoU: false, orthoV: false };

/** DXF/GLB: X=easting, Y=northing, Z=elevation → Three X=east, Y=up, Z=north */
const GLTF_DXF_TO_VIEWER = new THREE.Matrix4().set(1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 1);

/** DXF polylines become Line/LineSegments — same axis fix as Mesh or geometry stays in UTM XY plane and vanishes from collar-relative view. */
function applyGltfDxfToAllGeometry(root) {
  root.traverse((child) => {
    const geom = child.geometry;
    if (geom && typeof geom.applyMatrix4 === 'function') {
      geom.applyMatrix4(GLTF_DXF_TO_VIEWER);
      if (typeof geom.computeBoundingSphere === 'function') geom.computeBoundingSphere();
    }
  });
}

function brightenGltfLineMaterials(root) {
  const vis = 0x8ab4d4;
  root.traverse((child) => {
    if (!child.isLine && !child.isLineSegments && !child.isLineLoop) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((m) => {
      if (!m || !m.color) return;
      const { r, g, b } = m.color;
      if (r + g + b < 0.08) m.color.setHex(vis);
      m.needsUpdate = true;
    });
  });
}

/** Surface draw opacity (1 = opaque). ~0.8 reads as light transparency over terrain/traces. */
const GLTF_SURFACE_OPACITY = 0.8;

/** Fault DXF/GLB draw color (matches theme red). */
const GLTF_FAULT_COLOR = 0xef4444;

function applyGltfFaultColor(root, colorHex = GLTF_FAULT_COLOR) {
  root.traverse((child) => {
    if (!child.material) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((m) => {
      if (!m?.color) return;
      m.color.setHex(colorHex);
      m.needsUpdate = true;
    });
  });
}

/**
 * Apply uniform alpha to DXF/GLB materials (meshes, lines, points) for readability over terrain.
 * @param {THREE.Object3D} root
 * @param {number} [opacity=GLTF_SURFACE_OPACITY]
 */
function applyGltfSurfaceTransparency(root, opacity = GLTF_SURFACE_OPACITY) {
  const o = Math.max(0, Math.min(1, opacity));
  root.traverse((child) => {
    if (!child.material) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach((m) => {
      if (!m || typeof m.opacity !== 'number') return;
      m.transparent = o < 1 || Boolean(m.map) || Boolean(m.alphaMap);
      m.opacity = o;
      if (m.transparent) {
        m.depthWrite = false;
      }
      m.needsUpdate = true;
    });
  });
}

/**
 * Anchor-relative placement in viewer space (X east, Y up, Z north), same as terrain/traces.
 * When GLB vertices are already absolute UTM after the axis fix, geometry AABB center ≈ API bbox center
 * and this reduces to root.position = −collar in anchor space. When vertices are local, we align centroid to API bbox.
 *
 * @param {THREE.Object3D} root glTF scene root (axis fix already applied), position still (0,0,0)
 * @param {object} asset API row with bbox_* from dxf_gltf_service
 * @param {{ easting: number, northing: number, elevation?: number }} collarWorld survey collar (absolute CRS)
 * @param {{ easting: number, northing: number, elevation?: number }|null|undefined} sceneAnchorWorld if null, anchor = collarWorld
 */
function positionGltfRootCollarRelative(root, asset, collarWorld, sceneAnchorWorld) {
  const el = collarWorld.elevation ?? 0;
  const anchor = sceneAnchorWorld || collarWorld;
  const Ox = anchor.easting;
  const Oy = anchor.northing;
  const Oz = anchor.elevation ?? 0;
  const anchorAbsThree = new THREE.Vector3(Ox, Oz, Oy);

  const applyFallback = (mode) => {
    root.position.set(-Ox, -Oz, -Oy);
    root.userData.gltfPlacement = { mode };
  };

  root.updateMatrixWorld(true);
  const geomBox = new THREE.Box3().setFromObject(root);
  if (geomBox.isEmpty()) {
    applyFallback('empty_bounds_fallback_minus_collar');
    return;
  }

  const nums = [
    asset.bbox_min_x,
    asset.bbox_min_y,
    asset.bbox_min_z,
    asset.bbox_max_x,
    asset.bbox_max_y,
    asset.bbox_max_z,
  ];
  const hasBbox = nums.every((v) => v != null && Number.isFinite(Number(v)));

  const geomCenter = new THREE.Vector3();
  geomBox.getCenter(geomCenter);

  if (!hasBbox) {
    applyFallback('no_api_bbox_fallback_minus_collar');
    root.userData.gltfPlacement.geomCenterLocal = geomCenter.toArray();
    return;
  }

  const apiCenterAbs = new THREE.Vector3(
    (Number(asset.bbox_min_x) + Number(asset.bbox_max_x)) * 0.5,
    (Number(asset.bbox_min_z) + Number(asset.bbox_max_z)) * 0.5,
    (Number(asset.bbox_min_y) + Number(asset.bbox_max_y)) * 0.5,
  );

  const anchorDelta = new THREE.Vector3().copy(apiCenterAbs).sub(geomCenter).sub(anchorAbsThree);
  root.position.copy(anchorDelta);

  root.userData.gltfPlacement = {
    mode: 'bbox_center_anchor',
    apiCenterAbs: apiCenterAbs.toArray(),
    sceneAnchor: [Ox, Oy, Oz],
    geomCenterLocal: geomCenter.toArray(),
    anchorDeltaViewer: anchorDelta.toArray(),
    collarViewer: [collarWorld.easting - Ox, el - Oz, collarWorld.northing - Oy],
  };
}

/**
 * Console metadata for DXF/GLB debugging (filter DevTools by `[3D][GLTF]`).
 * @param {THREE.Object3D} root
 * @param {object} asset API row
 * @param {{ easting: number, northing: number, elevation?: number }} collarWorld
 * @param {{ byteLength?: number, extensionsUsed?: string[], extensionsRequired?: string[] }} [extras]
 */
function logGltfLoadMetadata(root, asset, collarWorld, extras = {}) {
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  if (!box.isEmpty()) {
    box.getCenter(center);
    box.getSize(size);
  }
  let meshes = 0;
  let lines = 0;
  let points = 0;
  let positionVerts = 0;
  root.traverse((c) => {
    if (c.isMesh) meshes += 1;
    if (c.isLineSegments || c.isLine || c.isLineLoop) lines += 1;
    if (c.isPoints) points += 1;
    const pos = c.geometry?.attributes?.position;
    if (pos) positionVerts += pos.count;
  });
  const apiBbox = {
    min: [asset.bbox_min_x, asset.bbox_min_y, asset.bbox_min_z].map((v) => (v != null ? Number(v) : null)),
    max: [asset.bbox_max_x, asset.bbox_max_y, asset.bbox_max_z].map((v) => (v != null ? Number(v) : null)),
  };
  // eslint-disable-next-line no-console
  console.groupCollapsed(`[3D][GLTF] ${asset.file_name || 'surface'} (asset_id=${asset.asset_id})`);
  // eslint-disable-next-line no-console
  console.log('collar used for root.position', collarWorld);
  // eslint-disable-next-line no-console
  console.log('root.position (collar-relative offset)', root.position.toArray());
  if (root.userData.gltfPlacement) {
    // eslint-disable-next-line no-console
    console.log('placement strategy', root.userData.gltfPlacement);
  }
  // eslint-disable-next-line no-console
  console.log('api bbox (UTM order: x east, y north, z elev)', apiBbox);
  // eslint-disable-next-line no-console
  console.log('world bounds after load (viewer space)', box.isEmpty()
    ? 'EMPTY — geometry may be missing or not in frustum'
    : { min: box.min.toArray(), max: box.max.toArray(), center: center.toArray(), size: size.toArray() });
  // eslint-disable-next-line no-console
  console.log('drawables', { meshes, lines, points, positionAttributeVertices: positionVerts });
  // eslint-disable-next-line no-console
  console.log('flags', {
    meshopt_compressed: asset.meshopt_compressed,
    vertex_count_db: asset.vertex_count,
    face_count_db: asset.face_count,
    file_size_glb: asset.file_size_glb,
    layer_names: asset.layer_names,
  });
  if (extras.byteLength != null) {
    // eslint-disable-next-line no-console
    console.log('glb bytes', extras.byteLength);
  }
  if (extras.extensionsUsed?.length || extras.extensionsRequired?.length) {
    // eslint-disable-next-line no-console
    console.log('glTF extensions', {
      used: extras.extensionsUsed,
      required: extras.extensionsRequired,
    });
  }
  // eslint-disable-next-line no-console
  console.groupEnd();
}

function getGltfAssetBasename(fileName) {
  if (!fileName || typeof fileName !== 'string') return '';
  return fileName.replace(/\.[^/.]+$/i, '').split(/[/\\]/).pop() || '';
}

function isLensGltfAsset(asset) {
  const base = getGltfAssetBasename(asset?.file_name);
  return base.includes('Live Lenses - ');
}

function isFaultGltfAsset(asset) {
  const base = getGltfAssetBasename(asset?.file_name);
  return base.startsWith('FLT_');
}

function isLoadableGltfAsset(asset) {
  return Boolean(asset?.glb_blob_url);
}

/** Auto-enable lens + fault GLBs that have blob URLs. */
function defaultLoadableLensFaultIds(assets) {
  return (assets || [])
    .filter((a) => isLoadableGltfAsset(a) && (isLensGltfAsset(a) || isFaultGltfAsset(a)))
    .map((a) => a.asset_id);
}

/**
 * Lens id for labels: last run of digits in the basename (e.g. "Live Lenses - 104.dxf" → "104").
 * @param {string|null|undefined} fileName
 * @returns {string|null}
 */
function extractGltfLensIdFromFileName(fileName) {
  if (!fileName || typeof fileName !== 'string') return null;
  const base = getGltfAssetBasename(fileName);
  const runs = base.match(/\d+/g);
  if (!runs?.length) return null;
  return runs[runs.length - 1];
}

/**
 * One CSS2D label at mesh AABB center. Box3 is in **world** space; label position under `root` must be **local**.
 * @param {THREE.Object3D} root glTF root after world placement
 * @param {{ file_name?: string|null, asset_id?: number }} asset
 */
function attachGltfSurfaceCss2dLabel(root, asset) {
  const fromName = extractGltfLensIdFromFileName(asset?.file_name);
  const idText = fromName ?? (asset?.asset_id != null ? String(asset.asset_id) : null);
  if (!idText) return;

  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  if (box.isEmpty()) return;

  const centerWorld = new THREE.Vector3();
  box.getCenter(centerWorld);
  const size = box.getSize(new THREE.Vector3());
  // World +Y nudge (viewer up), then convert to root-local for CSS2D child.
  centerWorld.y += Math.max(0.5, size.y * 0.04);
  const localPos = centerWorld.clone();
  root.worldToLocal(localPos);

  const el = document.createElement('div');
  el.className = 'gltf-surface-label';
  el.textContent = idText;
  el.setAttribute('title', asset?.file_name || idText);

  const label = new CSS2DObject(el);
  label.position.copy(localPos);
  label.center.set(0.5, 0.5);
  label.userData.isGltfSurfaceLabel = true;
  root.add(label);
}

function disposeGltfObject(root) {
  root.traverse((obj) => {
    if (obj instanceof CSS2DObject) {
      obj.element?.remove();
      return;
    }
    if (obj.geometry) {
      obj.geometry.dispose();
    }
    if (obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((mat) => {
        if (!mat) return;
        Object.keys(mat).forEach((key) => {
          const v = mat[key];
          if (v && typeof v === 'object' && v.isTexture) v.dispose();
        });
        mat.dispose();
      });
    }
  });
}

function removeAllGltfFromScene(scene, groupMap) {
  if (!scene || !groupMap) return;
  groupMap.forEach((group) => {
    scene.remove(group);
    disposeGltfObject(group);
  });
  groupMap.clear();
}

async function ensureMeshoptDecoder(loader) {
  const existing = loader.meshoptDecoder;
  if (existing?.supported) {
    if (existing.ready) await existing.ready;
    return;
  }
  try {
    const mod = await import('meshoptimizer/decoder');
    const MeshoptDecoder = mod.MeshoptDecoder ?? mod.default;
    if (!MeshoptDecoder) {
      // eslint-disable-next-line no-console
      console.error('[3D] meshoptimizer/decoder: missing MeshoptDecoder export');
      return;
    }
    if (MeshoptDecoder.ready) await MeshoptDecoder.ready;
    if (!MeshoptDecoder.supported) {
      // eslint-disable-next-line no-console
      console.error('[3D] MeshoptDecoder not supported (WebAssembly disabled or unavailable)');
      return;
    }
    // GLTFParser checks parser.options.meshoptDecoder; unsupported or missing → same error as "setMeshoptDecoder".
    if (typeof loader.setMeshoptDecoder === 'function') {
      loader.setMeshoptDecoder(MeshoptDecoder);
    } else {
      loader.meshoptDecoder = MeshoptDecoder;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[3D] Failed to load MeshoptDecoder:', e);
  }
}

/**
 * @param {object} asset API row
 * @param {{ easting: number, northing: number, elevation?: number }} collarWorld
 * @param {GLTFLoader} loader
 * @param {() => Promise<ArrayBuffer>} loadArrayBuffer GLB bytes (use API proxy, not direct blob URL — CORS)
 */
async function loadGltfSceneForViewer(asset, collarWorld, sceneAnchorWorld, loader, loadArrayBuffer) {
  // Always attach decoder before parse: DB flag can be wrong but glTF may still require EXT_meshopt_compression.
  await ensureMeshoptDecoder(loader);
  const buf = await loadArrayBuffer();
  const gltf = await new Promise((resolve, reject) => {
    try {
      loader.parse(buf, '', resolve, reject);
    } catch (e) {
      reject(e);
    }
  });
  const root = gltf.scene;
  applyGltfDxfToAllGeometry(root);
  if (isFaultGltfAsset(asset)) {
    applyGltfFaultColor(root);
  } else {
    brightenGltfLineMaterials(root);
  }
  applyGltfSurfaceTransparency(root);
  positionGltfRootCollarRelative(root, asset, collarWorld, sceneAnchorWorld);
  attachGltfSurfaceCss2dLabel(root, asset);
  root.name = `gltf_asset_${asset.asset_id}`;
  root.userData.gltfAssetId = asset.asset_id;

  const json = gltf.parser?.json;
  logGltfLoadMetadata(root, asset, collarWorld, {
    byteLength: buf.byteLength,
    extensionsUsed: json?.extensionsUsed,
    extensionsRequired: json?.extensionsRequired,
  });

  return root;
}

const DrillHole3DViewer = React.memo(({
  selectedHoleId,
  /**
   * Increment when the user selects a hole from the 2D map so we frame the 3D camera on that trace
   * after traces load (table / other selection paths can leave this unchanged).
   */
  focusTraceNonce = 0,
  /** When set, show top-right control to open/close separate window. */
  onPopoutToggle,
  /** True when viewer is shown in the popup (button becomes dock / X). */
  isPoppedOut = false,
}) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const css2dRendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const intervalMeshRef = useRef(null);
  const pointMeshRef = useRef(null);
  const cylinderGeomRef = useRef(null);
  const labelGroupRef = useRef(null);
  const traceLinesRef = useRef(new Map());
  const multiDistRef = useRef(new Map());
  const collarMarkersRef = useRef([]);
  const gridRef = useRef(null);
  const axesRef = useRef(null);
  const holeIdLabelsRef = useRef(null);
  const terrainMeshRef = useRef(null);
  /** @type {React.MutableRefObject<Map<number, THREE.Object3D>>} */
  const gltfGroupMapRef = useRef(new Map());
  /** @type {React.MutableRefObject<GLTFLoader|null>} */
  const gltfLoaderRef = useRef(null);
  /** Set in scene mount effect: sync camera + WebGL + CSS2D sizes to container (labels track resizes). */
  const syncCanvasToContainerRef = useRef(() => {});
  const prevHoleIdRef = useRef(null);
  const initialViewSetRef = useRef(false);
  /** Tracks hole_id for GLTF catalog so we only reset surface checkboxes when the hole changes, not when spatial radius changes. */
  const gltfCatalogHoleIdRef = useRef(null);
  /** Last non-empty catalog per hole (anti-wipe on bad refetch). */
  const gltfCatalogAssetsRef = useRef({ holeId: null, assets: [] });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTypes, setActiveTypes] = useState(new Set(['lithology']));
  /** Drillz geometry: active layer tableDefinitionIds */
  const [activeLayerIds, setActiveLayerIds] = useState(new Set());
  const [geometryScene, setGeometryScene] = useState(null);
  const [geometryVisualBundles, setGeometryVisualBundles] = useState(new Map());
  const [geometrySridWarning, setGeometrySridWarning] = useState(null);
  const [geometryRefreshing, setGeometryRefreshing] = useState(false);
  const geometryRefreshAttemptedRef = useRef(false);
  const geometryPollAbortRef = useRef(null);
  const visualsFetchKeyRef = useRef('');
  const [intervalDataMap, setIntervalDataMap] = useState(new Map());
  const [intervalLoading, setIntervalLoading] = useState(false);
  const [legendMap, setLegendMap] = useState(new Map());
  const [showLabels, setShowLabels] = useState(false);
  const [radiusScales, setRadiusScales] = useState({});

  const [spatialRadius, setSpatialRadius] = useState(0);
  const [azimuth, setAzimuth] = useState(0);
  const [sliceWidth, setSliceWidth] = useState(100);
  const [sliceEnabled, setSliceEnabled] = useState(false);
  const [multiTraceData, setMultiTraceData] = useState(null);
  const [holeCount, setHoleCount] = useState(0);
  const [spatialLoading, setSpatialLoading] = useState(false);
  const [collarWorld, setCollarWorld] = useState(null);
  const [orthophotoEnabled, setOrthophotoEnabled] = useState(false);
  /**
   * undefined = catalog fetch not finished yet (wait before XYZ ortho to avoid COG fallback flash).
   * @type {{ hasTileCog?: boolean, tileMinZoom?: number|null, tileMaxZoom?: number|null }|undefined}
   */
  const [orthoTileMeta, setOrthoTileMeta] = useState(undefined);
  const [terrainEnabled, setTerrainEnabled] = useState(true);
  const [terrainStyle, setTerrainStyle] = useState(/** @type {TerrainStyle} */ (TERRAIN_STYLE.WIREFRAME));
  const [terrainUndersideMode, setTerrainUndersideMode] = useState(getInitialTerrainUndersideMode);
  const [terrainLoading, setTerrainLoading] = useState(false);
  const [terrainError, setTerrainError] = useState(null);
  const [terrainMarginM, setTerrainMarginM] = useState(DEFAULT_TERRAIN_MARGIN_M);
  /** Shift terrain mesh along viewer +Y (m); GIS “Z” elevation in this scene. */
  const [terrainVerticalOffsetM, setTerrainVerticalOffsetM] = useState(DEFAULT_TERRAIN_VERTICAL_OFFSET_M);
  /** @type {Array<{ asset_id: number, file_name?: string|null, glb_blob_url: string, vertex_count?: number|null, face_count?: number|null, meshopt_compressed?: boolean, layer_names?: string[] }>} */
  const [gltfAssets, setGltfAssets] = useState([]);
  const [gltfCollarApi, setGltfCollarApi] = useState(null);
  const [gltfListLoading, setGltfListLoading] = useState(false);
  const [gltfAssetLoading, setGltfAssetLoading] = useState(false);
  const [loadedGltfAssetIds, setLoadedGltfAssetIds] = useState([]);
  const [showLensesPanel, setShowLensesPanel] = useState(false);
  const [showFaultsPanel, setShowFaultsPanel] = useState(false);
  const [showTerrainPanel, setShowTerrainPanel] = useState(false);
  const [showOrthophotoPanel, setShowOrthophotoPanel] = useState(false);
  /** Bumps when GLB instances are added/removed so Zoom button disabled state updates. */
  const [gltfInstanceEpoch, setGltfInstanceEpoch] = useState(0);
  const terrainStyleRef = useRef(terrainStyle);
  const terrainUndersideModeRef = useRef(terrainUndersideMode);
  const terrainVerticalOffsetMRef = useRef(DEFAULT_TERRAIN_VERTICAL_OFFSET_M);
  const applyTerrainMarginForCamRef = useRef(() => {});

  const activeTypesArray = useMemo(
    () => (USE_DRILLZ_GEOMETRY
      ? [...activeLayerIds]
      : [...activeTypes].filter(id => DOWNHOLE_SOURCES[id])),
    [activeTypes, activeLayerIds],
  );

  const drillzLayerList = useMemo(() => {
    if (!USE_DRILLZ_GEOMETRY || !geometryScene?.layers) return [];
    return geometryScene.layers.map((ly) => {
      const bundle = geometryVisualBundles.get(ly.tableDefinitionId);
      return {
        id: ly.tableDefinitionId,
        code: ly.tableCode,
        label: bundle?.tableDisplayName || ly.tableCode || ly.tableDefinitionId,
        spatialMode: ly.spatialMode,
      };
    });
  }, [geometryScene, geometryVisualBundles]);

  const effectiveDemAssetId = CONFIGURED_DEM_ASSET_ID;
  const effectiveOrthoAssetId = CONFIGURED_ORTHO_ASSET_ID;

  const collarForGltf = useMemo(
    () => collarWorld || gltfCollarApi,
    [collarWorld, gltfCollarApi],
  );

  const sceneAnchorWorld = SCENE_FIXED_ORIGIN;

  const lensGltfAssets = useMemo(
    () => gltfAssets.filter(isLensGltfAsset),
    [gltfAssets],
  );
  const faultGltfAssets = useMemo(
    () => gltfAssets.filter(isFaultGltfAsset),
    [gltfAssets],
  );
  const lensLoadableAssetIds = useMemo(
    () => lensGltfAssets.filter(isLoadableGltfAsset).map((a) => a.asset_id),
    [lensGltfAssets],
  );
  const faultLoadableAssetIds = useMemo(
    () => faultGltfAssets.filter(isLoadableGltfAsset).map((a) => a.asset_id),
    [faultGltfAssets],
  );
  const allLensOn =
    lensLoadableAssetIds.length > 0
    && lensLoadableAssetIds.every((id) => loadedGltfAssetIds.includes(id));
  const allFaultsOn =
    faultLoadableAssetIds.length > 0
    && faultLoadableAssetIds.every((id) => loadedGltfAssetIds.includes(id));
  const lensesPanelActive = useMemo(
    () => lensLoadableAssetIds.some((id) => loadedGltfAssetIds.includes(id)),
    [lensLoadableAssetIds, loadedGltfAssetIds],
  );
  const faultsPanelActive = useMemo(
    () => faultLoadableAssetIds.some((id) => loadedGltfAssetIds.includes(id)),
    [faultLoadableAssetIds, loadedGltfAssetIds],
  );

  terrainStyleRef.current = terrainStyle;
  terrainUndersideModeRef.current = terrainUndersideMode;
  terrainVerticalOffsetMRef.current = terrainVerticalOffsetM;

  useEffect(() => {
    applyTerrainMarginForCamRef.current = () => {
      const cam = cameraRef.current;
      const ctl = controlsRef.current;
      if (!cam || !ctl) return;
      const margin = terrainMarginFromCameraDistance(cam.position.distanceTo(ctl.target));
      setTerrainMarginM((prev) => {
        if (Math.abs(prev - margin) < Math.max(70, prev * 0.15)) return prev;
        return margin;
      });
    };
  });

  const selectExclusive = useCallback((typeId) => {
    setActiveTypes(new Set([typeId]));
  }, []);

  const toggleType = useCallback((typeId) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  }, []);

  const toggleLayer = useCallback((tableDefinitionId) => {
    visualsFetchKeyRef.current = '';
    setActiveLayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(tableDefinitionId)) next.delete(tableDefinitionId);
      else next.add(tableDefinitionId);
      return next;
    });
  }, []);

  const clearTypes = useCallback(() => {
    setActiveTypes(new Set());
  }, []);

  const renderScene = useCallback(() => {
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      css2dRendererRef.current?.render(sceneRef.current, cameraRef.current);
    }
  }, []);

  const toggleGltfAsset = useCallback((assetId) => {
    setLoadedGltfAssetIds((prev) =>
      prev.includes(assetId) ? prev.filter((x) => x !== assetId) : [...prev, assetId],
    );
  }, []);

  const toggleAllGltfInGroup = useCallback((loadableIds) => {
    setLoadedGltfAssetIds((prev) => {
      if (loadableIds.length === 0) return prev;
      const allOn = loadableIds.every((id) => prev.includes(id));
      if (allOn) {
        const groupSet = new Set(loadableIds);
        return prev.filter((id) => !groupSet.has(id));
      }
      const next = new Set(prev);
      loadableIds.forEach((id) => next.add(id));
      return [...next];
    });
  }, []);

  const toggleAllLensAssets = useCallback(() => {
    toggleAllGltfInGroup(lensLoadableAssetIds);
  }, [lensLoadableAssetIds, toggleAllGltfInGroup]);

  const toggleAllFaultAssets = useCallback(() => {
    toggleAllGltfInGroup(faultLoadableAssetIds);
  }, [faultLoadableAssetIds, toggleAllGltfInGroup]);

  const closeToolbarPopoversOnOutsidePointer = useCallback(
    (e) => {
      if (!showLensesPanel && !showFaultsPanel && !showTerrainPanel && !showOrthophotoPanel) return;
      const t = e.target;
      const el = t instanceof Element ? t : t?.parentElement;
      if (el?.closest('.viewer-3d-toolbar')) return;
      setShowLensesPanel(false);
      setShowFaultsPanel(false);
      setShowTerrainPanel(false);
      setShowOrthophotoPanel(false);
    },
    [showLensesPanel, showFaultsPanel, showTerrainPanel, showOrthophotoPanel],
  );

  const zoomToGltfAsset = useCallback((assetId) => {
    const group = gltfGroupMapRef.current.get(assetId);
    if (!group) {
      // eslint-disable-next-line no-console
      console.warn('[3D][GLTF] Zoom: surface not loaded. Enable the checkbox first, wait for GLB…', { assetId });
      return;
    }
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;

    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    if (box.isEmpty()) {
      // eslint-disable-next-line no-console
      console.warn('[3D][GLTF] Zoom: world bounding box is empty', { assetId, name: group.name });
      return;
    }

    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z, 5);
    const dist = maxDim * 1.65;
    const dir = new THREE.Vector3(1, 0.7, 1).normalize();
    camera.position.copy(center).add(dir.multiplyScalar(dist));
    camera.up.set(0, 1, 0);
    camera.lookAt(center);
    controls.target.copy(center);
    controls.update();
    applyTerrainMarginForCamRef.current();
    renderScene();
    // eslint-disable-next-line no-console
    console.log('[3D][GLTF] Zoom framed', {
      assetId,
      center: center.toArray(),
      size: size.toArray(),
      cameraPos: camera.position.toArray(),
    });
  }, [renderScene]);

  const logGltfAssetToConsole = useCallback((assetId) => {
    const group = gltfGroupMapRef.current.get(assetId);
    const asset = gltfAssets.find((x) => x.asset_id === assetId);
    const collar = collarForGltf;
    if (!group || !asset || !collar) {
      // eslint-disable-next-line no-console
      console.warn('[3D][GLTF] Log: need loaded surface + catalog row + collar', { assetId, hasGroup: !!group, hasAsset: !!asset, hasCollar: !!collar });
      return;
    }
    logGltfLoadMetadata(group, asset, collar, {});
  }, [gltfAssets, collarForGltf]);

  const handleSnapView = useCallback((direction) => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;

    const box = new THREE.Box3();
    traceLinesRef.current.forEach(line => {
      line.geometry.computeBoundingBox();
      if (line.geometry.boundingBox) box.union(line.geometry.boundingBox);
    });

    const center = new THREE.Vector3();
    let dist = 200;

    if (!box.isEmpty()) {
      const size = new THREE.Vector3();
      box.getCenter(center);
      box.getSize(size);
      dist = Math.max(size.x, size.y, size.z) * 1.5;
    }

    camera.up.set(0, 1, 0);

    switch (direction) {
      case 'n':
        camera.position.set(center.x, center.y, center.z + dist);
        break;
      case 's':
        camera.position.set(center.x, center.y, center.z - dist);
        break;
      case 'e':
        camera.position.set(center.x + dist, center.y, center.z);
        break;
      case 'w':
        camera.position.set(center.x - dist, center.y, center.z);
        break;
      case 'plan':
        camera.position.set(center.x, center.y + dist, center.z);
        camera.up.set(0, 0, -1);
        break;
      default:
        return;
    }

    camera.lookAt(center);
    controls.target.copy(center);
    controls.update();
    applyTerrainMarginForCamRef.current();
    renderScene();
  }, [renderScene]);

  /** Orbit camera to fit the selected hole’s trace (used after map pick). */
  const frameSelectedTraceCamera = useCallback(() => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls || !selectedHoleId) return;
    const line = traceLinesRef.current.get(selectedHoleId);
    if (!line) return;
    line.geometry.computeBoundingBox();
    const bb = line.geometry.boundingBox;
    if (!bb || bb.isEmpty()) return;
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    bb.getCenter(center);
    bb.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z, 5);
    const dist = maxDim * 1.65;
    const dir = new THREE.Vector3(1, 0.7, 1).normalize();
    camera.up.set(0, 1, 0);
    camera.position.copy(center).add(dir.multiplyScalar(dist));
    camera.lookAt(center);
    controls.target.copy(center);
    controls.update();
    applyTerrainMarginForCamRef.current();
    renderScene();
  }, [selectedHoleId, renderScene]);

  const selectedTraceInfo = useMemo(() => {
    if (!multiTraceData) return null;
    const hole = multiTraceData.find(h => h.hole_id === selectedHoleId);
    if (!hole?.points?.length) return null;
    const pts = hole.points;
    return {
      pointCount: pts.length,
      depth: Math.abs(pts[pts.length - 1].z),
    };
  }, [multiTraceData, selectedHoleId]);

  const totalIntervalCount = useMemo(() => {
    if (USE_DRILLZ_GEOMETRY && geometryScene?.layers) {
      let count = 0;
      for (const ly of geometryScene.layers) {
        if (!activeLayerIds.has(ly.tableDefinitionId)) continue;
        count += (ly.features || []).length;
      }
      return count;
    }
    let count = 0;
    for (const [, arr] of intervalDataMap) count += arr.length;
    return count;
  }, [intervalDataMap, geometryScene, activeLayerIds]);

  // ── Scene setup (mount only) ──────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth || 300;
    const height = container.clientHeight || 300;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 10000);
    camera.position.set(120, 120, 120);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height, true);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x030712);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const css2dRenderer = new CSS2DRenderer();
    css2dRenderer.setSize(width, height);
    css2dRenderer.domElement.style.position = 'absolute';
    css2dRenderer.domElement.style.top = '0';
    css2dRenderer.domElement.style.left = '0';
    css2dRenderer.domElement.style.pointerEvents = 'none';
    css2dRenderer.domElement.style.zIndex = '1';
    container.appendChild(css2dRenderer.domElement);
    css2dRendererRef.current = css2dRenderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = false;
    let terrainMarginTimer = null;
    const debouncedTerrainMargin = () => {
      clearTimeout(terrainMarginTimer);
      terrainMarginTimer = setTimeout(() => applyTerrainMarginForCamRef.current(), 400);
    };
    controls.addEventListener('change', () => {
      renderer.render(scene, camera);
      css2dRenderer.render(scene, camera);
      debouncedTerrainMargin();
    });
    controlsRef.current = controls;

    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(10, 10, 5);
    scene.add(dirLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
    fillLight.position.set(-5, -10, -5);
    scene.add(fillLight);

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));

    const axes = new THREE.AxesHelper(50);
    scene.add(axes);
    axesRef.current = axes;

    const grid = new THREE.GridHelper(200, 20);
    scene.add(grid);
    gridRef.current = grid;

    const collarGeom = new THREE.SphereGeometry(2, 16, 16);
    const collarMat = new THREE.MeshStandardMaterial({ color: SELECTED_COLOR });
    scene.add(new THREE.Mesh(collarGeom, collarMat));

    renderer.render(scene, camera);
    css2dRenderer.render(scene, camera);

    // Keep WebGL canvas CSS size and CSS2D overlay pixel size in lockstep with clientWidth/Height.
    // setSize(..., true) sets canvas inline dimensions so we are not relying only on CSS 100%.
    let resizeRafId = null;
    let zeroSizeRetries = 0;
    const ZERO_SIZE_RETRY_MAX = 90;
    const applyResize = () => {
      resizeRafId = null;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) {
        if (zeroSizeRetries < ZERO_SIZE_RETRY_MAX) {
          zeroSizeRetries += 1;
          resizeRafId = requestAnimationFrame(applyResize);
        }
        return;
      }
      zeroSizeRetries = 0;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, true);
      css2dRenderer.setSize(w, h);
      renderer.render(scene, camera);
      css2dRenderer.render(scene, camera);
    };
    const scheduleResize = () => {
      if (resizeRafId !== null) return;
      resizeRafId = requestAnimationFrame(applyResize);
    };

    const resizeObserver = new ResizeObserver(scheduleResize);
    try {
      resizeObserver.observe(container, { box: 'border-box' });
    } catch {
      resizeObserver.observe(container);
    }
    window.addEventListener('resize', scheduleResize);

    syncCanvasToContainerRef.current = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(applyResize);
      });
    };

    return () => {
      syncCanvasToContainerRef.current = () => {};
      if (resizeRafId !== null) cancelAnimationFrame(resizeRafId);
      clearTimeout(terrainMarginTimer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleResize);
      controls.dispose();

      traceLinesRef.current.forEach(line => {
        line.geometry.dispose();
        line.material.dispose();
      });
      traceLinesRef.current.clear();
      multiDistRef.current.clear();

      collarMarkersRef.current.forEach(m => {
        m.geometry.dispose();
        m.material.dispose();
      });
      collarMarkersRef.current = [];

      if (intervalMeshRef.current) {
        intervalMeshRef.current.material?.dispose();
        intervalMeshRef.current = null;
      }
      if (pointMeshRef.current) {
        pointMeshRef.current.material?.dispose();
        pointMeshRef.current = null;
      }
      if (cylinderGeomRef.current) {
        cylinderGeomRef.current.dispose();
        cylinderGeomRef.current = null;
      }
      if (labelGroupRef.current) {
        labelGroupRef.current.traverse(child => {
          if (child instanceof CSS2DObject) child.element.remove();
        });
        labelGroupRef.current = null;
      }
      if (holeIdLabelsRef.current) {
        holeIdLabelsRef.current.traverse(child => {
          if (child instanceof CSS2DObject) child.element.remove();
        });
        holeIdLabelsRef.current = null;
      }
      if (gridRef.current) {
        gridRef.current.geometry.dispose();
        gridRef.current.material.dispose();
        gridRef.current = null;
      }
      if (terrainMeshRef.current) {
        scene.remove(terrainMeshRef.current);
        terrainMeshRef.current.geometry.dispose();
        const tm = terrainMeshRef.current.material;
        tm.map?.dispose();
        tm.dispose();
        terrainMeshRef.current = null;
      }
      if (axesRef.current) {
        axesRef.current.dispose();
        axesRef.current = null;
      }
      removeAllGltfFromScene(scene, gltfGroupMapRef.current);
      gltfLoaderRef.current = null;
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      if (container.contains(css2dRenderer.domElement)) container.removeChild(css2dRenderer.domElement);
      rendererRef.current = null;
      css2dRendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;
    };
  }, []);

  // ── Reset raster pick when hole changes ───────────────────────────────
  useEffect(() => {
    setCollarWorld(null);
    setTerrainError(null);
    setTerrainMarginM(DEFAULT_TERRAIN_MARGIN_M);
  }, [selectedHoleId]);

  // ── Orthophoto tile catalog (RasterTileApi / tile_cog_blob_url) ─────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await rasterAssetsService.getAuthenticatedHeaders();
        const url = rasterAssetsService.getTilingAssetDetailUrl(CONFIGURED_ORTHO_ASSET_ID);
        const res = await fetch(url, { method: 'GET', headers });
        if (cancelled) return;
        if (!res.ok) {
          setOrthoTileMeta({ hasTileCog: false, tileMinZoom: null, tileMaxZoom: null });
          return;
        }
        const data = await res.json();
        const a = data.asset;
        if (cancelled) return;
        if (!a) {
          setOrthoTileMeta({ hasTileCog: false, tileMinZoom: null, tileMaxZoom: null });
          return;
        }
        setOrthoTileMeta({
          hasTileCog: Boolean(a.hasTileCog),
          tileMinZoom: a.tileMinZoom ?? null,
          tileMaxZoom: a.tileMaxZoom ?? null,
        });
      } catch {
        if (!cancelled) setOrthoTileMeta({ hasTileCog: false, tileMinZoom: null, tileMaxZoom: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Fetch trace data (Drillz geometry or legacy nearby_traces) ───────
  useEffect(() => {
    if (!selectedHoleId) {
      setMultiTraceData(null);
      setHoleCount(0);
      setCollarWorld(null);
      setGeometryScene(null);
      setGeometryVisualBundles(new Map());
      setActiveLayerIds(new Set());
      geometryRefreshAttemptedRef.current = false;
      return;
    }

    if (!USE_DRILLZ_GEOMETRY) {
      let cancelled = false;
      const fetchTraces = async () => {
        setLoading(true);
        if (spatialRadius > 0) setSpatialLoading(true);
        setError(null);

        try {
          const effectiveAzimuth = sliceEnabled ? azimuth : null;
          const effectiveSliceWidth = sliceEnabled ? sliceWidth : null;

          const result = await drillingService.getNearbyTraces(
            selectedHoleId, spatialRadius, effectiveAzimuth, effectiveSliceWidth,
          );
          if (!cancelled) {
            setMultiTraceData(result.data);
            setHoleCount(result.holeCount);
            setCollarWorld(result.collarWorld || null);
          }
        } catch (err) {
          console.error('[3D] getNearbyTraces failed, falling back to single trace:', err);
          try {
            const points = await drillingService.getDrillTrace(selectedHoleId);
            if (!cancelled) {
              setMultiTraceData(points?.length > 0 ? [{ hole_id: selectedHoleId, points }] : null);
              setHoleCount(points?.length > 0 ? 1 : 0);
              setCollarWorld(null);
            }
          } catch (fallbackErr) {
            if (!cancelled) {
              setError(fallbackErr.message);
              setMultiTraceData(null);
              setHoleCount(0);
              setCollarWorld(null);
            }
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
            setSpatialLoading(false);
          }
        }
      };

      fetchTraces();
      return () => { cancelled = true; };
    }

    let cancelled = false;

    const applySceneToState = (scene) => {
      const sceneEpsg = scene?.crs?.epsg;
      if (sceneEpsg && sceneEpsg !== CONFIGURED_DEM_NATIVE_EPSG) {
        setGeometrySridWarning(
          `Trace SRID EPSG:${sceneEpsg} differs from DEM EPSG:${CONFIGURED_DEM_NATIVE_EPSG}; terrain may misalign.`,
        );
      } else {
        setGeometrySridWarning(null);
      }

      setGeometryScene(scene);
      setHoleCount(Math.max(scene.traces?.length ?? 0, (scene.collars || []).length));

      const selectedCollar = findSelectedCollar(scene.collars || [], selectedHoleId);
      setCollarWorld(selectedCollar ? collarToWorld(selectedCollar) : null);

      const collarByUuid = new Map(
        (scene.collars || []).map((c) => [c.collarUuid, c]),
      );
      let legacyShape = (scene.traces || []).map((t) => ({
        hole_id: t.holeId || t.collarUuid,
        points: (t.positions || []).map(([e, n, z]) => {
          const collar = collarByUuid.get(t.collarUuid);
          const [ce, cn, cz] = collar?.position || [e, n, z];
          return {
            x: e - ce,
            y: n - cn,
            z: z - cz,
          };
        }),
        _drillzTrace: t,
      }));

      if (!legacyShape.length && (scene.collars || []).length) {
        legacyShape = (scene.collars || []).map((c) => {
          const positions = syntheticTraceFromLayers(scene, c.collarUuid);
          const [ce, cn, cz] = c.position || [0, 0, 0];
          const points = (positions || []).map(([e, n, z]) => ({
            x: e - ce,
            y: n - cn,
            z: z - cz,
          }));
          return {
            hole_id: c.holeId || c.collarUuid,
            points,
            _syntheticTrace: true,
          };
        }).filter((h) => h.points.length >= 2);
      }

      setMultiTraceData(legacyShape.length ? legacyShape : null);

      const layerIds = (scene.layers || []).map((l) => l.tableDefinitionId);
      if (layerIds.length) {
        setActiveLayerIds((prev) => {
          if (prev.size > 0) return prev;
          const first = layerIds[0];
          return new Set(first ? [first] : []);
        });
      }
    };

    const loadScene = async () => {
      setLoading(true);
      if (spatialRadius > 0) setSpatialLoading(true);
      setError(null);
      setGeometrySridWarning(null);

      if (geometryPollAbortRef.current) {
        geometryPollAbortRef.current.abort();
        geometryPollAbortRef.current = null;
      }

      const fetchScene = () => geometryService.fetchGeometryScene({
        holeId: selectedHoleId,
        radius: spatialRadius,
        azimuth: sliceEnabled ? azimuth : null,
        sliceWidth: sliceEnabled ? sliceWidth : null,
      });

      try {
        let scene = await fetchScene();
        // eslint-disable-next-line no-console
        console.info('[3D] Drillz geometry scene loaded', {
          holeId: selectedHoleId,
          traces: scene?.traces?.length ?? 0,
          layers: scene?.layers?.length ?? 0,
          pendingRefresh: scene?.freshness?.pendingRefresh ?? false,
          crs: scene?.crs,
        });

        if (cancelled) return;

        const hasRenderableContent = (scene.traces?.length ?? 0) > 0
          || (scene.layers || []).some((ly) => (ly.features?.length ?? 0) > 0);

        if (scene?.freshness?.pendingRefresh && !geometryRefreshAttemptedRef.current && !hasRenderableContent) {
          geometryRefreshAttemptedRef.current = true;
          setGeometryRefreshing(true);
          geometryService.refreshGeometry({ holeId: selectedHoleId })
            .catch((e) => console.warn('[3D] geometry refresh failed:', e))
            .finally(() => setGeometryRefreshing(false));
        }

        applySceneToState(scene);

        if (scene?.freshness?.pendingRefresh && !hasRenderableContent) {
          const pollAbort = new AbortController();
          geometryPollAbortRef.current = pollAbort;
          geometryService.pollUntilFreshInBackground(fetchScene, {
            signal: pollAbort.signal,
            maxAttempts: 8,
            onDone: (freshScene) => {
              if (cancelled || pollAbort.signal.aborted) return;
              setGeometryRefreshing(false);
              applySceneToState(freshScene);
            },
          });
        } else {
          setGeometryRefreshing(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[3D] drillz geometry scene failed:', err);
          setError(err.message || String(err));
          setGeometryScene(null);
          setMultiTraceData(null);
          setHoleCount(0);
          setCollarWorld(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSpatialLoading(false);
        }
      }
    };

    loadScene();

    return () => {
      cancelled = true;
      if (geometryPollAbortRef.current) {
        geometryPollAbortRef.current.abort();
        geometryPollAbortRef.current = null;
      }
    };
  }, [selectedHoleId, spatialRadius, azimuth, sliceWidth, sliceEnabled]);

  // ── Lazy-load Drillz layer visuals (active layers × all scene collars) ─
  useEffect(() => {
    if (!USE_DRILLZ_GEOMETRY || !geometryScene) return undefined;

    const collarUuids = [
      ...new Set([
        ...(geometryScene.collars || []).map((c) => c.collarUuid),
        ...(geometryScene.traces || []).map((t) => t.collarUuid),
      ]),
    ].filter(Boolean);
    const layerIds = [...activeLayerIds];
    if (!collarUuids.length || !layerIds.length) return undefined;

    const fetchKey = `${collarUuids.slice().sort().join(',')}|${layerIds.slice().sort().join(',')}`;
    if (visualsFetchKeyRef.current === fetchKey) return undefined;

    let cancelled = false;
    setIntervalLoading(true);

    (async () => {
      try {
        const visuals = await geometryService.fetchGeometryVisuals(collarUuids, layerIds);
        if (cancelled) return;
        visualsFetchKeyRef.current = fetchKey;
        setGeometryVisualBundles((prev) => {
          const next = new Map(prev);
          (visuals.layers || []).forEach((ly) => {
            next.set(ly.tableDefinitionId, buildLayerVisualBundle(ly));
          });
          return next;
        });
      } catch (e) {
        console.error('[3D] geometry visuals failed:', e);
      } finally {
        if (!cancelled) setIntervalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [geometryScene, activeLayerIds]);

  // Reset visuals cache when hole changes
  useEffect(() => {
    visualsFetchKeyRef.current = '';
    if (!selectedHoleId) {
      setGeometryVisualBundles(new Map());
    }
  }, [selectedHoleId]);

  // ── GLTF surface asset catalog (dxf_gltf_service) ─────────────────────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!selectedHoleId) {
      removeAllGltfFromScene(scene, gltfGroupMapRef.current);
      setGltfInstanceEpoch((e) => e + 1);
      setGltfAssets([]);
      setGltfCollarApi(null);
      setLoadedGltfAssetIds([]);
      setShowLensesPanel(false);
      setShowFaultsPanel(false);
      gltfCatalogHoleIdRef.current = null;
      gltfCatalogAssetsRef.current = { holeId: null, assets: [] };
      return;
    }

    const prevCatalogHole = gltfCatalogHoleIdRef.current;
    const holeChanged = prevCatalogHole !== selectedHoleId;
    gltfCatalogHoleIdRef.current = selectedHoleId;

    removeAllGltfFromScene(scene, gltfGroupMapRef.current);
    setGltfInstanceEpoch((e) => e + 1);

    if (holeChanged) {
      setLoadedGltfAssetIds([]);
      setShowLensesPanel(false);
      setShowFaultsPanel(false);
      gltfCatalogAssetsRef.current = { holeId: selectedHoleId, assets: [] };
    }

    let cancelled = false;
    const effRadius = Math.max(spatialRadius, 500);
    (async () => {
      setGltfListLoading(true);
      try {
        const r = await drillingService.getGltfAssets(selectedHoleId, effRadius);
        if (cancelled) return;
        let assets = r.assets || [];
        const cached = gltfCatalogAssetsRef.current;
        if (
          assets.length === 0
          && cached.holeId === selectedHoleId
          && cached.assets.length > 0
        ) {
          // eslint-disable-next-line no-console
          console.warn('[3D][GLTF] catalog refetch empty; keeping previous catalog');
          assets = cached.assets;
        } else if (assets.length > 0) {
          gltfCatalogAssetsRef.current = { holeId: selectedHoleId, assets };
        }
        setGltfAssets(assets);
        setGltfCollarApi(r.collarWorld);
        setLoadedGltfAssetIds((prev) => {
          const valid = new Set(assets.map((a) => a.asset_id));
          const autoIds = defaultLoadableLensFaultIds(assets);
          if (holeChanged || (prev.length === 0 && autoIds.length > 0)) {
            return autoIds;
          }
          return prev.filter((id) => valid.has(id));
        });
        // eslint-disable-next-line no-console
        console.groupCollapsed(
          `[3D][GLTF] catalog hole=${selectedHoleId} radius_m=${effRadius} surfaces=${assets.length}`,
        );
        // eslint-disable-next-line no-console
        console.log('collarWorld (API)', r.collarWorld);
        const preview = assets.slice(0, 25);
        preview.forEach((a) => {
          // eslint-disable-next-line no-console
          console.log(`  ${a.file_name || a.asset_id}`, {
            asset_id: a.asset_id,
            bbox: {
              min: [a.bbox_min_x, a.bbox_min_y, a.bbox_min_z],
              max: [a.bbox_max_x, a.bbox_max_y, a.bbox_max_z],
            },
            vtx: a.vertex_count,
            meshopt: a.meshopt_compressed,
            glb_url: a.glb_blob_url ? '(set)' : '(missing)',
          });
        });
        if (assets.length > preview.length) {
          // eslint-disable-next-line no-console
          console.log(`… ${assets.length - preview.length} more (truncated)`);
        }
        // eslint-disable-next-line no-console
        console.groupEnd();
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('[3D][GLTF] getGltfAssets failed:', e);
          setGltfAssets([]);
          setGltfCollarApi(null);
        }
      } finally {
        if (!cancelled) setGltfListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedHoleId, spatialRadius]);

  // Re-fetch GLTF catalog when Drillz collar arrives but initial mv_traces lookup was empty
  useEffect(() => {
    if (!selectedHoleId || !collarWorld || gltfListLoading) return undefined;
    if ((gltfAssets || []).length > 0) return undefined;

    let cancelled = false;
    const effRadius = Math.max(spatialRadius, 500);
    (async () => {
      try {
        const r = await drillingService.getGltfAssets(
          selectedHoleId,
          effRadius,
          collarWorld,
          { forceClientCollar: true },
        );
        if (cancelled || !(r.assets || []).length) return;
        const assets = r.assets;
        gltfCatalogAssetsRef.current = { holeId: selectedHoleId, assets };
        setGltfAssets(assets);
        setGltfCollarApi(r.collarWorld);
        setLoadedGltfAssetIds((prev) => {
          if (prev.length) return prev;
          return defaultLoadableLensFaultIds(assets);
        });
      } catch (e) {
        console.warn('[3D][GLTF] collar retry failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedHoleId, collarWorld, spatialRadius, gltfAssets, gltfListLoading]);

  // Re-position loaded GLTF roots when Drillz placement collar arrives (no re-download).
  useEffect(() => {
    const collar = collarForGltf;
    if (!collar || loadedGltfAssetIds.length === 0) return;
    const map = gltfGroupMapRef.current;
    let moved = false;
    for (const id of loadedGltfAssetIds) {
      const root = map.get(id);
      const asset = gltfAssets.find((a) => a.asset_id === id);
      if (!root || !asset) continue;
      positionGltfRootCollarRelative(root, asset, collar, sceneAnchorWorld);
      moved = true;
    }
    if (moved) {
      setGltfInstanceEpoch((e) => e + 1);
      renderScene();
    }
  }, [
    collarForGltf?.easting,
    collarForGltf?.northing,
    collarForGltf?.elevation,
    loadedGltfAssetIds,
    gltfAssets,
    renderScene,
  ]);

  // ── Load / unload GLB meshes when toggled ─────────────────────────────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const collar = collarForGltf;
    const map = gltfGroupMapRef.current;
    const loadedSet = new Set(loadedGltfAssetIds);
    let removedAny = false;

    for (const [id, group] of [...map.entries()]) {
      if (!loadedSet.has(id)) {
        scene.remove(group);
        disposeGltfObject(group);
        map.delete(id);
        removedAny = true;
      }
    }
    if (removedAny) setGltfInstanceEpoch((e) => e + 1);

    if (!collar || loadedGltfAssetIds.length === 0) {
      renderScene();
      return undefined;
    }

    if (!gltfLoaderRef.current) {
      gltfLoaderRef.current = new GLTFLoader();
    }
    const loader = gltfLoaderRef.current;

    let cancelled = false;
    (async () => {
      let addedAny = false;
      for (const id of loadedGltfAssetIds) {
        if (cancelled || map.has(id)) continue;
        const asset = gltfAssets.find((a) => a.asset_id === id);
        if (!asset?.glb_blob_url) continue;
        setGltfAssetLoading(true);
        try {
          const root = await loadGltfSceneForViewer(asset, collar, sceneAnchorWorld, loader, () =>
            drillingService.fetchGltfGlbArrayBuffer(id),
          );
          if (cancelled) {
            disposeGltfObject(root);
            break;
          }
          scene.add(root);
          map.set(id, root);
          addedAny = true;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[3D] GLTF load failed', id, err);
        } finally {
          setGltfAssetLoading(false);
        }
      }
      if (addedAny && !cancelled) setGltfInstanceEpoch((e) => e + 1);
      renderScene();
    })();

    return () => {
      cancelled = true;
    };
  }, [loadedGltfAssetIds, gltfAssets, collarForGltf, renderScene]);

  // ── COG DEM terrain (collar-relative, native CRS ROI) ────────────────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (terrainMeshRef.current) {
      scene.remove(terrainMeshRef.current);
      terrainMeshRef.current.geometry.dispose();
      const prevMat = terrainMeshRef.current.material;
      prevMat.map?.dispose();
      prevMat.dispose();
      terrainMeshRef.current = null;
    }

    if (!terrainEnabled || !effectiveDemAssetId || !collarWorld) {
      if (process.env.NODE_ENV === 'development' && terrainEnabled && effectiveDemAssetId && !collarWorld) {
        // eslint-disable-next-line no-console
        console.warn(
          '[DrillHole3DViewer] Terrain skipped: no collar_world (use getNearbyTraces, not drill_traces-only fallback).',
        );
      }
      renderScene();
      return;
    }

    const roi = computeTerrainRoiBounds(collarWorld, multiTraceData, terrainMarginM);
    if (!roi) {
      renderScene();
      return;
    }

    const anchorWorld = SCENE_FIXED_ORIGIN;

    let cancelled = false;
    (async () => {
      setTerrainLoading(true);
      setTerrainError(null);
      try {
        const fetchHeaders = await rasterAssetsService.getAuthenticatedHeaders();
        const cogUrl = rasterAssetsService.getCogProxyUrl(effectiveDemAssetId);
        const bbox = [roi.minX, roi.minY, roi.maxX, roi.maxY];
        const orthoCatalogHasTiles =
          orthoTileMeta !== undefined && orthoTileMeta.hasTileCog === true;
        const skipOrthoCogProxy =
          ORTHO_USE_XYZ_TILES &&
          orthophotoEnabled &&
          effectiveOrthoAssetId != null &&
          orthoTileMeta !== undefined &&
          !orthoCatalogHasTiles;
        const orthoCogUrl =
          effectiveOrthoAssetId != null && !skipOrthoCogProxy
            ? rasterAssetsService.getCogProxyUrl(effectiveOrthoAssetId)
            : null;
        const applyOrthoTexture =
          orthophotoEnabled &&
          effectiveOrthoAssetId != null &&
          (!ORTHO_USE_XYZ_TILES || orthoTileMeta !== undefined);
        const mesh = await buildDemTerrainMesh({
          cogUrl,
          fetchHeaders,
          bbox,
          anchorWorld,
          gridSegments: computeAdaptiveGridSegments(roi),
          orthoCogUrl,
          applyOrthoTexture,
          nativeEpsg: CONFIGURED_DEM_NATIVE_EPSG,
          orthoAssetId: effectiveOrthoAssetId,
          orthoUseXyzTiles: ORTHO_USE_XYZ_TILES,
          orthoTileMeta: orthoTileMeta === undefined ? null : orthoTileMeta,
          getRasterTileUrl: (id, z, x, y) =>
            rasterAssetsService.getRasterTileProxyUrl(id, z, x, y),
          orthoXyzTmsY: ORTHO_XYZ_TMS_Y,
          orthoUseServerWarpWindow: ORTHO_USE_SERVER_WARP_WINDOW,
          getOrthophotoWindowUrl: (id, b, srid, w, h) =>
            rasterAssetsService.getOrthophotoWindowProxyUrl(id, b, srid, w, h),
        });
        if (cancelled) {
          if (mesh) {
            mesh.geometry.dispose();
            mesh.material.map?.dispose();
            mesh.material.dispose();
          }
          return;
        }
        if (!mesh) return;
        terrainMeshRef.current = mesh;
        mesh.renderOrder = -1;
        applyTerrainMaterialStyle(mesh, terrainStyleRef.current);
        applyTerrainUndersideUniforms(mesh, terrainUndersideModeRef.current);
        applyTerrainDebugFlips(mesh, NO_TERRAIN_DEBUG_FLIPS);
        mesh.position.y = terrainVerticalOffsetMRef.current;
        scene.add(mesh);
        syncCanvasToContainerRef.current();
        if (process.env.NODE_ENV === 'development') {
          mesh.geometry.computeBoundingBox();
          const bc = new THREE.Vector3();
          mesh.geometry.boundingBox.getCenter(bc);
          // eslint-disable-next-line no-console
          console.info(
            '[DrillHole3DViewer] terrain bbox center (fixed-origin-relative E,Up,N):',
            bc.x.toFixed(2),
            bc.y.toFixed(2),
            bc.z.toFixed(2),
            '| origin:', SCENE_FIXED_ORIGIN.easting, SCENE_FIXED_ORIGIN.northing,
            '| DEM EPSG:', CONFIGURED_DEM_NATIVE_EPSG,
          );
        }
      } catch (e) {
        if (!cancelled) setTerrainError(e?.message || String(e));
      } finally {
        if (!cancelled) {
          setTerrainLoading(false);
          syncCanvasToContainerRef.current();
        }
        renderScene();
      }
    })();

    return () => { cancelled = true; };
  }, [
    terrainEnabled,
    orthophotoEnabled,
    orthoTileMeta,
    effectiveDemAssetId,
    effectiveOrthoAssetId,
    collarWorld,
    multiTraceData,
    terrainMarginM,
    renderScene,
  ]);

  // ── Terrain material: soft solid or wireframe triangles ──────────────
  useEffect(() => {
    const mesh = terrainMeshRef.current;
    if (mesh) {
      applyTerrainMaterialStyle(mesh, terrainStyle);
      renderScene();
    }
  }, [terrainStyle, terrainEnabled, renderScene]);

  // ── Ortho underside / shader debug (uniforms on draped terrain only) ───
  useEffect(() => {
    const mesh = terrainMeshRef.current;
    if (mesh) {
      applyTerrainUndersideUniforms(mesh, terrainUndersideMode);
      renderScene();
    }
  }, [terrainUndersideMode, terrainEnabled, renderScene]);

  // ── Terrain vertical offset (collar-relative elevation / viewer +Y) ─────
  useEffect(() => {
    const mesh = terrainMeshRef.current;
    if (!mesh) return;
    mesh.position.y = terrainVerticalOffsetM;
    renderScene();
  }, [terrainVerticalOffsetM, renderScene]);

  // ── Build trace lines + distance tables + auto-frame camera ───────────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    traceLinesRef.current.forEach(line => {
      scene.remove(line);
      line.geometry.dispose();
      line.material.dispose();
    });
    traceLinesRef.current.clear();
    multiDistRef.current.clear();

    collarMarkersRef.current.forEach(m => {
      scene.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    });
    collarMarkersRef.current = [];

    if (!multiTraceData || multiTraceData.length === 0) {
      if (!(USE_DRILLZ_GEOMETRY && geometryScene)) {
        renderScene();
        return;
      }
    }

    const buildFromDrillz = USE_DRILLZ_GEOMETRY && geometryScene;

    if (buildFromDrillz) {
      const traces = buildTraceLines(geometryScene, selectedHoleId, SCENE_FIXED_ORIGIN);
      for (const t of traces) {
        const holeKey = t.holeId || t.collarUuid;
        const table = t.depthsM?.length === t.points.length
          ? Float64Array.from(t.depthsM)
          : buildDistanceTable(t.points);
        multiDistRef.current.set(holeKey, { table, points: t.points });

        const geometry = new THREE.BufferGeometry().setFromPoints(t.points);
        const material = new THREE.LineBasicMaterial({
          color: t.isSelected ? SELECTED_COLOR : NEARBY_COLOR,
          linewidth: 2,
        });
        const line = new THREE.Line(geometry, material);
        scene.add(line);
        traceLinesRef.current.set(holeKey, line);

        if (!t.isSelected && t.points.length) {
          const markerGeom = new THREE.SphereGeometry(1.5, 12, 12);
          const markerMat = new THREE.MeshStandardMaterial({ color: NEARBY_COLOR });
          const marker = new THREE.Mesh(markerGeom, markerMat);
          marker.position.copy(t.points[0]);
          scene.add(marker);
          collarMarkersRef.current.push(marker);
        }
      }
    } else {
      const Ox = SCENE_FIXED_ORIGIN.easting;
      const Oy = SCENE_FIXED_ORIGIN.northing;
      const Oz = SCENE_FIXED_ORIGIN.elevation;
      const cx = collarWorld ? collarWorld.easting : 0;
      const cy = collarWorld ? collarWorld.northing : 0;
      const cz = collarWorld ? (collarWorld.elevation ?? 0) : 0;

      for (const holeData of multiTraceData) {
        const { hole_id, points: rawPoints } = holeData;
        if (!rawPoints || rawPoints.length < 2) continue;

        const threePoints = rawPoints.map(
          (p) => new THREE.Vector3(
            p.x + cx - Ox,
            p.z + cz - Oz,
            p.y + cy - Oy,
          ),
        );

        const table = buildDistanceTable(threePoints);
        multiDistRef.current.set(hole_id, { table, points: threePoints });

        const isSelected = hole_id === selectedHoleId;
        const geometry = new THREE.BufferGeometry().setFromPoints(threePoints);
        const material = new THREE.LineBasicMaterial({
          color: isSelected ? SELECTED_COLOR : NEARBY_COLOR,
          linewidth: 2,
        });
        const line = new THREE.Line(geometry, material);
        scene.add(line);
        traceLinesRef.current.set(hole_id, line);

        if (!isSelected) {
          const markerGeom = new THREE.SphereGeometry(1.5, 12, 12);
          const markerMat = new THREE.MeshStandardMaterial({ color: NEARBY_COLOR });
          const marker = new THREE.Mesh(markerGeom, markerMat);
          marker.position.copy(threePoints[0]);
          scene.add(marker);
          collarMarkersRef.current.push(marker);
        }
      }
    }

    const holeChanged = prevHoleIdRef.current !== selectedHoleId;
    prevHoleIdRef.current = selectedHoleId;

    if (holeChanged && multiTraceData.length > 1) {
      const box = new THREE.Box3();
      traceLinesRef.current.forEach(line => {
        line.geometry.computeBoundingBox();
        if (line.geometry.boundingBox) box.union(line.geometry.boundingBox);
      });

      if (!box.isEmpty()) {
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        box.getCenter(center);
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const dist = maxDim * 1.2;

        const camera = cameraRef.current;
        if (camera) {
          camera.position.set(
            center.x + dist * 0.5,
            center.y + dist * 0.5,
            center.z + dist * 0.5
          );
          camera.lookAt(center);
          if (controlsRef.current) {
            controlsRef.current.target.copy(center);
            controlsRef.current.update();
          }
          applyTerrainMarginForCamRef.current();
        }
      }
    }

    if (!initialViewSetRef.current && (multiTraceData?.length > 0 || buildFromDrillz)) {
      initialViewSetRef.current = true;
      handleSnapView('n');
    }

    renderScene();
  }, [multiTraceData, selectedHoleId, collarWorld, geometryScene, renderScene, handleSnapView]);

  // ── Frame camera on map-selected hole once traces are in the scene ─────
  useEffect(() => {
    if (!focusTraceNonce) return;
    if (!selectedHoleId || loading || spatialLoading) return;
    if (!multiTraceData?.some((h) => h.hole_id === selectedHoleId)) return;
    if (!traceLinesRef.current.get(selectedHoleId)) return;
    frameSelectedTraceCamera();
  }, [
    focusTraceNonce,
    selectedHoleId,
    loading,
    spatialLoading,
    multiTraceData,
    frameSelectedTraceCamera,
  ]);

  // ── Dynamic grid + axes that match spatial radius / trace extent ──────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (gridRef.current) {
      scene.remove(gridRef.current);
      gridRef.current.geometry.dispose();
      gridRef.current.material.dispose();
    }
    if (axesRef.current) {
      scene.remove(axesRef.current);
      axesRef.current.dispose();
    }

    let gridSize;
    if (spatialRadius > 0) {
      gridSize = spatialRadius * 2;
    } else if (multiTraceData?.length > 0) {
      const box = new THREE.Box3();
      for (const line of traceLinesRef.current.values()) {
        line.geometry.computeBoundingBox();
        if (line.geometry.boundingBox) box.union(line.geometry.boundingBox);
      }
      if (!box.isEmpty()) {
        const size = new THREE.Vector3();
        box.getSize(size);
        gridSize = Math.max(size.x, size.z, 200) * 1.2;
      } else {
        gridSize = 200;
      }
    } else {
      gridSize = 200;
    }

    const hideFloorGrid = terrainEnabled;

    if (sliceEnabled && spatialRadius > 0 && sliceWidth > 0) {
      const along = spatialRadius * 2;
      const across = sliceWidth;
      const divisionsX = Math.max(4, Math.round(along / 20));
      const divisionsZ = Math.max(2, Math.round(across / 20));

      const planeGeom = new THREE.PlaneGeometry(along, across, divisionsX, divisionsZ);
      planeGeom.rotateX(-Math.PI / 2);

      const wireMat = new THREE.MeshBasicMaterial({
        color: 0x374151, wireframe: true, transparent: true, opacity: 0.5,
      });
      const sliceGrid = new THREE.Mesh(planeGeom, wireMat);
      sliceGrid.rotation.y = -azimuth * (Math.PI / 180);

      scene.add(sliceGrid);
      gridRef.current = sliceGrid;
    } else if (!hideFloorGrid) {
      const divisions = Math.max(10, Math.round(gridSize / 20));
      const newGrid = new THREE.GridHelper(gridSize, divisions);
      scene.add(newGrid);
      gridRef.current = newGrid;
    } else {
      gridRef.current = null;
    }

    const newAxes = new THREE.AxesHelper(gridSize * 0.3);
    scene.add(newAxes);
    axesRef.current = newAxes;

    renderScene();
  }, [spatialRadius, sliceEnabled, sliceWidth, azimuth, multiTraceData, terrainEnabled, renderScene]);

  // ── Hole-ID labels at max depth of each trace ────────────────────────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (holeIdLabelsRef.current) {
      scene.remove(holeIdLabelsRef.current);
      holeIdLabelsRef.current.traverse(child => {
        if (child instanceof CSS2DObject) child.element.remove();
      });
      holeIdLabelsRef.current = null;
    }

    if (!multiTraceData || multiTraceData.length === 0) return;

    const group = new THREE.Group();

    for (const holeData of multiTraceData) {
      const holeTrace = multiDistRef.current.get(holeData.hole_id);
      if (!holeTrace || holeTrace.points.length < 2) continue;

      const lastPoint = holeTrace.points[holeTrace.points.length - 1];
      const totalDepth = holeTrace.table[holeTrace.table.length - 1];

      const wrapper = document.createElement('div');
      wrapper.className = 'hole-id-label';
      if (holeData.hole_id === selectedHoleId) wrapper.classList.add('selected');
      wrapper.textContent = holeData.hole_id;

      const popup = document.createElement('div');
      popup.className = 'hole-id-popup';
      popup.innerHTML = `<div><strong>${holeData.hole_id}</strong></div><div>Depth: ${totalDepth.toFixed(1)}m</div>`;
      wrapper.appendChild(popup);

      const label = new CSS2DObject(wrapper);
      label.position.copy(lastPoint);
      group.add(label);
    }

    scene.add(group);
    holeIdLabelsRef.current = group;
    renderScene();
  }, [multiTraceData, selectedHoleId, renderScene]);

  // ── Fetch downhole interval data (legacy path only) ─────────────────
  useEffect(() => {
    if (USE_DRILLZ_GEOMETRY) {
      setIntervalDataMap(new Map());
      setLegendMap(new Map());
      return;
    }

    if (!multiTraceData || multiTraceData.length === 0 || activeTypesArray.length === 0) {
      setIntervalDataMap(new Map());
      setLegendMap(new Map());
      return;
    }

    const holeIds = multiTraceData.map(h => h.hole_id);
    let cancelled = false;

    (async () => {
      setIntervalLoading(true);
      try {
        const entries = await Promise.all(
          activeTypesArray.map(async (typeId) => {
            const data = await drillingService.getDownholeData(
              holeIds.length === 1 ? holeIds[0] : holeIds,
              typeId
            );
            return [typeId, data];
          })
        );
        if (!cancelled) {
          const dataMap = new Map(entries);
          setIntervalDataMap(dataMap);

          const legends = new Map();
          for (const [typeId, data] of dataMap) {
            const legend = buildLegendForSource(typeId, data);
            if (legend) legends.set(typeId, legend);
          }
          setLegendMap(legends);
        }
      } catch (err) {
        console.error('[3D] Error loading downhole data:', err);
        if (!cancelled) {
          setIntervalDataMap(new Map());
          setLegendMap(new Map());
        }
      } finally {
        if (!cancelled) setIntervalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [multiTraceData, activeTypesArray]);

  // ── Build combined InstancedMesh (legacy downhole or Drillz geometry layers)
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (intervalMeshRef.current) {
      scene.remove(intervalMeshRef.current);
      intervalMeshRef.current.material?.dispose();
      intervalMeshRef.current = null;
    }
    if (pointMeshRef.current) {
      scene.remove(pointMeshRef.current);
      pointMeshRef.current.material?.dispose();
      pointMeshRef.current = null;
    }

    if (USE_DRILLZ_GEOMETRY && geometryScene) {
      if (activeLayerIds.size === 0) {
        renderScene();
        return;
      }

      const rawSegments = buildGeometrySegments(
        geometryScene,
        geometryVisualBundles,
        activeLayerIds,
        selectedHoleId,
        SCENE_FIXED_ORIGIN,
      );
      const intervalSegs = rawSegments.filter((s) => s.kind === 'interval');
      const pointSegs = rawSegments.filter((s) => s.kind === 'point');
      const channelSegs = assignLayerChannels(
        intervalSegs.filter((s) => s.length >= 0.01),
      );

      if (!cylinderGeomRef.current) {
        cylinderGeomRef.current = new THREE.CylinderGeometry(1, 1, 1, 8);
      }

      if (channelSegs.length) {
        const material = new THREE.MeshStandardMaterial({});
        const mesh = new THREE.InstancedMesh(cylinderGeomRef.current, material, channelSegs.length);
        const matrix = new THREE.Matrix4();
        const color = new THREE.Color();
        const grayColor = new THREE.Color(NEARBY_COLOR);

        channelSegs.forEach((seg, i) => {
          const needsOffset = seg.totalChannels > 1;
          let midpoint = seg.midpoint.clone();
          let quat = seg.quaternion.clone();
          if (needsOffset) {
            const tangent = new THREE.Vector3().subVectors(seg.end, seg.start).normalize();
            const offsetDir = new THREE.Vector3().crossVectors(tangent, new THREE.Vector3(0, 1, 0));
            if (offsetDir.lengthSq() < 1e-6) offsetDir.set(1, 0, 0);
            offsetDir.normalize();
            const ring = (seg.channelIndex - (seg.totalChannels - 1) / 2) * SATELLITE_OFFSET;
            midpoint.add(offsetDir.multiplyScalar(ring));
          }
          const baseRadius = TUBE_RADIUS * (seg.isSelected ? 1 : 0.7) * (needsOffset ? 0.7 : 1);
          const instanceRadius = baseRadius * (seg.radiusMul ?? 1);
          matrix.compose(
            midpoint,
            quat,
            new THREE.Vector3(instanceRadius, seg.length, instanceRadius),
          );
          mesh.setMatrixAt(i, matrix);
          color.set(seg.color);
          if (!seg.isSelected) color.lerp(grayColor, 0.3);
          mesh.setColorAt(i, color);
        });

        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        scene.add(mesh);
        intervalMeshRef.current = mesh;
      }

      if (pointSegs.length) {
        const sphereGeom = new THREE.SphereGeometry(0.6, 8, 8);
        const material = new THREE.MeshStandardMaterial({});
        const mesh = new THREE.InstancedMesh(sphereGeom, material, pointSegs.length);
        const matrix = new THREE.Matrix4();
        const color = new THREE.Color();
        pointSegs.forEach((seg, i) => {
          matrix.compose(seg.position, new THREE.Quaternion(), new THREE.Vector3(1, 1, 1));
          mesh.setMatrixAt(i, matrix);
          color.set(seg.color);
          mesh.setColorAt(i, color);
        });
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        scene.add(mesh);
        pointMeshRef.current = mesh;
      }

      renderScene();
      return;
    }

    if (intervalDataMap.size === 0 || multiDistRef.current.size === 0) {
      renderScene();
      return;
    }

    const channelEntries = assignChannels(intervalDataMap, activeTypesArray);
    if (channelEntries.length === 0) { renderScene(); return; }

    if (!cylinderGeomRef.current) {
      cylinderGeomRef.current = new THREE.CylinderGeometry(1, 1, 1, 8);
    }

    const count = channelEntries.length;
    const material = new THREE.MeshStandardMaterial({});
    const mesh = new THREE.InstancedMesh(cylinderGeomRef.current, material, count);

    const matrix = new THREE.Matrix4();
    const color = new THREE.Color();
    const grayColor = new THREE.Color(NEARBY_COLOR);

    for (let i = 0; i < count; i++) {
      const { interval, sourceId, channelIndex, totalChannels, holeId } = channelEntries[i];
      const holeTrace = multiDistRef.current.get(holeId);
      if (!holeTrace) continue;

      const { table: distTable, points } = holeTrace;
      const fromDepth = parseFloat(interval.from_depth);
      const toDepth = parseFloat(interval.to_depth);
      if (isNaN(fromDepth) || isNaN(toDepth)) continue;

      const needsOffset = totalChannels > 1;
      const result = needsOffset
        ? interpolateIntervalWithOffset(distTable, points, fromDepth, toDepth, channelIndex, totalChannels, SATELLITE_OFFSET)
        : interpolateInterval(distTable, points, fromDepth, toDepth);
      if (result.length < 0.01) continue;

      const isSelected = holeId === selectedHoleId;
      const baseRadius = TUBE_RADIUS * (isSelected ? 1 : 0.7) * (needsOffset ? 0.7 : 1);
      const legend = legendMap.get(sourceId);
      const rScale = radiusScales[sourceId] ?? 1;
      const rMult = getRadiusMultiplier(interval, legend, rScale);
      const instanceRadius = baseRadius * rMult;

      matrix.compose(
        result.midpoint,
        result.quaternion,
        new THREE.Vector3(instanceRadius, result.length, instanceRadius)
      );
      mesh.setMatrixAt(i, matrix);

      setIntervalThreeColor(color, interval, sourceId, legend);
      if (!isSelected) color.lerp(grayColor, 0.3);
      mesh.setColorAt(i, color);
    }

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

    scene.add(mesh);
    intervalMeshRef.current = mesh;
    renderScene();
  }, [
    intervalDataMap,
    activeTypesArray,
    activeLayerIds,
    geometryScene,
    geometryVisualBundles,
    multiTraceData,
    radiusScales,
    selectedHoleId,
    legendMap,
    renderScene,
  ]);

  // ── Build CSS2D callout labels (selected hole only) ───────────────────
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (labelGroupRef.current) {
      scene.remove(labelGroupRef.current);
      labelGroupRef.current.traverse(child => {
        if (child instanceof CSS2DObject) child.element.remove();
      });
      labelGroupRef.current = null;
    }

    if (!showLabels || intervalDataMap.size === 0 || legendMap.size === 0) {
      renderScene();
      return;
    }

    const holeTrace = multiDistRef.current.get(selectedHoleId);
    if (!holeTrace) { renderScene(); return; }
    const { table: distTable, points } = holeTrace;
    if (points.length < 2) { renderScene(); return; }

    const multiMode = activeTypesArray.length > 1;
    const group = new THREE.Group();
    let labelIdx = 0;

    for (const sourceId of activeTypesArray) {
      const legend = legendMap.get(sourceId);
      if (!legend) continue;
      const src = DOWNHOLE_SOURCES[sourceId];

      const intervals = (intervalDataMap.get(sourceId) || []).filter(d => d.hole_id === selectedHoleId);
      if (intervals.length === 0) continue;

      const step = intervals.length > MAX_LABELS
        ? Math.ceil(intervals.length / MAX_LABELS) : 1;

      for (let i = 0; i < intervals.length; i += step) {
        const interval = intervals[i];
        const fromDepth = parseFloat(interval.from_depth);
        const toDepth = parseFloat(interval.to_depth);
        if (isNaN(fromDepth) || isNaN(toDepth)) continue;

        const result = interpolateInterval(distTable, points, fromDepth, toDepth);
        if (result.length < 0.01) continue;

        const intervalColor = getIntervalColor(interval, sourceId, legend);
        const side = labelIdx % 2 === 0 ? 'label-right' : 'label-left';

        const valueText = multiMode
          ? `[${src.label}] ${src.labelFormatter(interval)}`
          : src.labelFormatter(interval);
        const depthText = `${fromDepth.toFixed(0)}-${toDepth.toFixed(0)}m`;

        const wrapper = document.createElement('div');
        wrapper.className = 'downhole-label-wrapper';

        const div = document.createElement('div');
        div.className = `downhole-label ${side}`;
        div.style.borderColor = intervalColor;
        div.innerHTML =
          `<span class="label-value">${valueText}</span>` +
          `<span class="label-depth">${depthText}</span>`;

        wrapper.appendChild(div);
        const label = new CSS2DObject(wrapper);
        label.position.copy(result.midpoint);
        group.add(label);

        labelIdx++;
      }
    }

    scene.add(group);
    labelGroupRef.current = group;
    renderScene();
  }, [showLabels, intervalDataMap, activeTypesArray, legendMap, multiTraceData, selectedHoleId, renderScene]);

  const toggleLensesPanel = useCallback(() => {
    setShowLensesPanel((prev) => {
      const next = !prev;
      if (next) {
        setShowFaultsPanel(false);
        setShowTerrainPanel(false);
        setShowOrthophotoPanel(false);
      }
      return next;
    });
  }, []);

  const toggleFaultsPanel = useCallback(() => {
    setShowFaultsPanel((prev) => {
      const next = !prev;
      if (next) {
        setShowLensesPanel(false);
        setShowTerrainPanel(false);
        setShowOrthophotoPanel(false);
      }
      return next;
    });
  }, []);

  // ── Render ────────────────────────────────────────────────────────────
  const renderGltfAssetPanel = ({
    panelKey,
    buttonLabel,
    panelTitle,
    assets,
    showPanel,
    onTogglePanel,
    loadableIds,
    allOn,
    toggleAll,
    panelActive,
    loadAllTitle,
    unloadAllTitle,
    buttonTitle,
    ariaLabel,
  }) => (
    <div className="viewer-3d-gltf-wrap">
      <span className="viewer-3d-toolbar-sep" />
      <button
        type="button"
        className={`viewer-3d-toggle ${panelActive ? 'active' : ''}`}
        onClick={onTogglePanel}
        disabled={!collarForGltf}
        title={!collarForGltf ? 'Need collar coordinates to place surfaces' : buttonTitle}
      >
        {buttonLabel}
      </button>
      {showPanel && (
        <div className="viewer-3d-gltf-panel" role="listbox" aria-label={ariaLabel}>
          <div className="viewer-3d-gltf-panel-header">
            <div className="viewer-3d-gltf-panel-title">{panelTitle}</div>
            <button
              type="button"
              className="viewer-3d-gltf-all-btn"
              onClick={toggleAll}
              disabled={!collarForGltf || loadableIds.length === 0 || gltfListLoading}
              title={allOn ? unloadAllTitle : loadAllTitle}
            >
              {allOn ? 'None' : 'All'}
            </button>
          </div>
          {gltfListLoading && (
            <div className="viewer-3d-gltf-panel-hint">Loading list…</div>
          )}
          {assets.map((a) => {
            void gltfInstanceEpoch;
            const cbId = `viewer-3d-gltf-cb-${panelKey}-${a.asset_id}`;
            const inScene = gltfGroupMapRef.current.has(a.asset_id);
            return (
              <div key={a.asset_id} className="viewer-3d-gltf-row">
                <input
                  id={cbId}
                  className="viewer-3d-gltf-cb"
                  type="checkbox"
                  checked={loadedGltfAssetIds.includes(a.asset_id)}
                  onChange={() => toggleGltfAsset(a.asset_id)}
                  disabled={!collarForGltf}
                />
                <label htmlFor={cbId} className="viewer-3d-gltf-text-label">
                  <span className="viewer-3d-gltf-name">
                    {a.file_name || `Asset ${a.asset_id}`}
                  </span>
                  <span className="viewer-3d-gltf-meta">
                    {a.vertex_count != null ? `${a.vertex_count.toLocaleString()} vtx` : ''}
                    {a.meshopt_compressed ? ' · meshopt' : ''}
                  </span>
                </label>
                <div className="viewer-3d-gltf-actions">
                  <button
                    type="button"
                    className="viewer-3d-gltf-action-btn"
                    disabled={!inScene}
                    title={
                      inScene
                        ? 'Frame camera on this surface (viewer space)'
                        : 'Load the surface first (checkbox), then zoom'
                    }
                    onClick={() => zoomToGltfAsset(a.asset_id)}
                  >
                    Zoom
                  </button>
                  <button
                    type="button"
                    className="viewer-3d-gltf-action-btn"
                    disabled={!inScene}
                    title="Re-print load metadata to the console ([3D][GLTF])"
                    onClick={() => logGltfAssetToConsole(a.asset_id)}
                  >
                    Log
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="drill-hole-3d-viewer" onPointerDownCapture={closeToolbarPopoversOnOutsidePointer}>
      <div className="viewer-3d-canvas-area" ref={containerRef}>
        {(loading || spatialLoading || geometryRefreshing) && (
          <div className="viewer-3d-loading">
            {geometryRefreshing
              ? 'Refreshing Drillz geometry…'
              : spatialLoading
                ? `Loading nearby holes (${spatialRadius}m)...`
                : `Loading trace for ${selectedHoleId}...`}
          </div>
        )}

        {error && !loading && (
          <div className="viewer-3d-error">{error}</div>
        )}

        {geometrySridWarning && (
          <div className="viewer-3d-terrain-error" title={geometrySridWarning}>
            {geometrySridWarning}
          </div>
        )}

        <div className="viewer-3d-toolbar">
          {USE_DRILLZ_GEOMETRY ? (
            <>
              <button
                className={`viewer-3d-toggle ${activeLayerIds.size === 0 ? 'active' : ''}`}
                onClick={() => setActiveLayerIds(new Set())}
                disabled={intervalLoading}
              >
                Trace
              </button>
              {drillzLayerList.map((ly) => (
                <button
                  key={ly.id}
                  className={`viewer-3d-toggle ${activeLayerIds.has(ly.id) ? 'active' : ''}`}
                  onClick={() => toggleLayer(ly.id)}
                  title={ly.spatialMode === 'point' ? 'Point layer' : 'Interval layer'}
                  disabled={intervalLoading}
                >
                  {ly.label}
                </button>
              ))}
            </>
          ) : (
            <>
              <button
                className={`viewer-3d-toggle ${activeTypes.size === 0 ? 'active' : ''}`}
                onClick={clearTypes}
                disabled={intervalLoading}
              >
                Trace
              </button>
              {SOURCE_IDS.map(id => (
                <button
                  key={id}
                  className={`viewer-3d-toggle ${activeTypes.has(id) ? 'active' : ''}`}
                  onClick={() => selectExclusive(id)}
                  onContextMenu={(e) => { e.preventDefault(); toggleType(id); }}
                  title="Click: show only | Right-click: add/remove"
                  disabled={intervalLoading}
                >
                  {DOWNHOLE_SOURCES[id].label}
                </button>
              ))}
            </>
          )}
          <span className="viewer-3d-toolbar-sep" />
          <button
            className={`viewer-3d-toggle ${showLabels ? 'active' : ''}`}
            onClick={() => setShowLabels(prev => !prev)}
            disabled={USE_DRILLZ_GEOMETRY ? activeLayerIds.size === 0 : activeTypes.size === 0}
          >
            Labels
          </button>
          <span className="viewer-3d-toolbar-sep" />
          <div className="viewer-3d-toolbar-pop-wrap">
            <button
              type="button"
              className={`viewer-3d-toggle ${terrainEnabled ? 'active' : ''}`}
              onClick={() => {
                setShowTerrainPanel((p) => {
                  const next = !p;
                  if (next) {
                    setShowLensesPanel(false);
                    setShowFaultsPanel(false);
                    setShowOrthophotoPanel(false);
                  }
                  return next;
                });
              }}
              disabled={!collarWorld}
              aria-expanded={showTerrainPanel}
              title={
                !collarWorld
                  ? 'Terrain needs collar coordinates from trace API'
                  : 'Terrain options: DEM on/off, display style, vertical offset'
              }
            >
              Terrain
            </button>
            {showTerrainPanel && (
              <div className="viewer-3d-toolbar-popover" role="dialog" aria-label="Terrain options">
                <div className="viewer-3d-toolbar-popover-header">
                  <div className="viewer-3d-toolbar-popover-title">Terrain</div>
                </div>
                <div className="viewer-3d-toolbar-popover-body">
                  <label className="viewer-3d-popover-check-row">
                    <input
                      type="checkbox"
                      checked={terrainEnabled}
                      onChange={(e) => setTerrainEnabled(e.target.checked)}
                    />
                    <span>Show DEM</span>
                  </label>
                  <div className="viewer-3d-popover-field">
                    <span className="viewer-3d-popover-field-label">Display</span>
                    <select
                      className="viewer-3d-terrain-style-select viewer-3d-terrain-style-select--popover"
                      value={terrainStyle}
                      onChange={(e) => setTerrainStyle(e.target.value)}
                      disabled={!terrainEnabled}
                      title="Translucent shaded surface or triangle wireframe"
                      aria-label="Terrain display style"
                    >
                      <option value={TERRAIN_STYLE.SOLID}>Terrain (soft)</option>
                      <option value={TERRAIN_STYLE.WIREFRAME}>Triangles</option>
                    </select>
                  </div>
                  <div className="viewer-3d-popover-field">
                    <div className="viewer-3d-terrain-z-wrap viewer-3d-terrain-z-wrap--popover">
                      <div className="viewer-3d-terrain-z-row">
                        <span className="viewer-3d-terrain-z-label">ΔZ (m)</span>
                        <span className="viewer-3d-terrain-z-value">{terrainVerticalOffsetM.toFixed(1)}</span>
                      </div>
                      <input
                        type="range"
                        className="viewer-3d-terrain-z-slider viewer-3d-terrain-z-slider--popover"
                        min={TERRAIN_VERTICAL_OFFSET_MIN_M}
                        max={TERRAIN_VERTICAL_OFFSET_MAX_M}
                        step={0.1}
                        value={terrainVerticalOffsetM}
                        onChange={(e) => setTerrainVerticalOffsetM(parseFloat(e.target.value))}
                        disabled={!terrainEnabled}
                        title="Shift DEM vertically (viewer +Y = elevation / GIS Z). Range ±10 m."
                        aria-label="Terrain vertical offset meters"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="viewer-3d-toolbar-pop-wrap">
            <button
              type="button"
              className={`viewer-3d-toggle ${orthophotoEnabled ? 'active' : ''}`}
              onClick={() => {
                setShowOrthophotoPanel((p) => {
                  const next = !p;
                  if (next) {
                    setShowLensesPanel(false);
                    setShowFaultsPanel(false);
                    setShowTerrainPanel(false);
                  }
                  return next;
                });
              }}
              disabled={!terrainEnabled || !effectiveOrthoAssetId || !collarWorld}
              aria-expanded={showOrthophotoPanel}
              title={
                !effectiveOrthoAssetId
                  ? 'Orthophoto asset id not configured'
                  : ORTHO_USE_XYZ_TILES && orthoTileMeta !== undefined && !orthoTileMeta.hasTileCog
                    ? 'Web Mercator tile COG not ready — run RasterTilingService backfill (tile_cog_blob_url)'
                    : ORTHO_USE_XYZ_TILES
                      ? 'Orthophoto options: drape imagery, underside shading'
                      : 'Orthophoto options: drape imagery (COG; may be slow for large rasters), underside'
              }
            >
              Orthophoto
            </button>
            {showOrthophotoPanel && (
              <div className="viewer-3d-toolbar-popover" role="dialog" aria-label="Orthophoto options">
                <div className="viewer-3d-toolbar-popover-header">
                  <div className="viewer-3d-toolbar-popover-title">Orthophoto</div>
                </div>
                <div className="viewer-3d-toolbar-popover-body">
                  <label className="viewer-3d-popover-check-row">
                    <input
                      type="checkbox"
                      checked={orthophotoEnabled}
                      disabled={!terrainEnabled || !effectiveOrthoAssetId || !collarWorld}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setOrthophotoEnabled(next);
                        if (next) setTerrainStyle(TERRAIN_STYLE.SOLID);
                      }}
                    />
                    <span>Drape orthophoto</span>
                  </label>
                  <div className="viewer-3d-popover-field">
                    <span className="viewer-3d-popover-field-label">Underside / debug</span>
                    <select
                      className="viewer-3d-terrain-style-select viewer-3d-terrain-style-select--popover"
                      value={terrainUndersideMode}
                      onChange={(e) => setTerrainUndersideMode(e.target.value)}
                      disabled={!terrainEnabled || !orthophotoEnabled}
                      title={
                        !orthophotoEnabled
                          ? 'Turn on orthophoto drape to enable underside options'
                          : 'Underside uses triangle back faces (Terrain soft). Triangles (wireframe) may not shade underside on all GPUs.'
                      }
                      aria-label="Orthophoto underside and debug"
                    >
                      <option value={TERRAIN_UNDERSIDE_MODE.ON}>Underside: on</option>
                      <option value={TERRAIN_UNDERSIDE_MODE.OFF}>Underside: off</option>
                      <option value={TERRAIN_UNDERSIDE_MODE.DEBUG_NORMALS}>Debug: world normals</option>
                      <option value={TERRAIN_UNDERSIDE_MODE.DEBUG_UNDERSIDE}>Debug: underside test</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
          {lensGltfAssets.length > 0 && renderGltfAssetPanel({
            panelKey: 'lenses',
            buttonLabel: `Lenses (${lensGltfAssets.length})`,
            panelTitle: 'Nearby lenses',
            assets: lensGltfAssets,
            showPanel: showLensesPanel,
            onTogglePanel: toggleLensesPanel,
            loadableIds: lensLoadableAssetIds,
            allOn: allLensOn,
            toggleAll: toggleAllLensAssets,
            panelActive: lensesPanelActive,
            loadAllTitle: 'Load all lenses with GLB',
            unloadAllTitle: 'Unload all lenses',
            buttonTitle: 'Toggle DXF/GLB lenses',
            ariaLabel: 'GLTF lenses',
          })}
          {faultGltfAssets.length > 0 && renderGltfAssetPanel({
            panelKey: 'faults',
            buttonLabel: `Faults (${faultGltfAssets.length})`,
            panelTitle: 'Nearby faults',
            assets: faultGltfAssets,
            showPanel: showFaultsPanel,
            onTogglePanel: toggleFaultsPanel,
            loadableIds: faultLoadableAssetIds,
            allOn: allFaultsOn,
            toggleAll: toggleAllFaultAssets,
            panelActive: faultsPanelActive,
            loadAllTitle: 'Load all faults with GLB',
            unloadAllTitle: 'Unload all faults',
            buttonTitle: 'Toggle DXF/GLB faults',
            ariaLabel: 'GLTF faults',
          })}
          {gltfAssetLoading && (
            <span className="viewer-3d-interval-status" title="Loading GLB">
              GLB…
            </span>
          )}
          {terrainLoading && (
            <span className="viewer-3d-interval-status">Terrain…</span>
          )}
          {terrainError && (
            <span className="viewer-3d-terrain-error" title={terrainError}>Terrain error</span>
          )}
          {intervalLoading && (
            <span className="viewer-3d-interval-status">Loading...</span>
          )}
        </div>

        {onPopoutToggle && (
          <button
            type="button"
            className={`viewer-3d-fullscreen-btn${isPoppedOut ? ' viewer-3d-fullscreen-btn--dock' : ''}`}
            onClick={onPopoutToggle}
            title={isPoppedOut ? 'Dock in dashboard' : 'Open in separate window'}
            aria-label={isPoppedOut ? 'Dock 3D viewer in dashboard' : 'Open 3D viewer in separate window'}
          >
            {isPoppedOut ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
        )}

        {(legendMap.size > 0 || (USE_DRILLZ_GEOMETRY && geometryVisualBundles.size > 0)) && (
          <div className="viewer-3d-color-legend">
            {USE_DRILLZ_GEOMETRY
              ? activeTypesArray.map((layerId) => {
                const bundle = geometryVisualBundles.get(layerId);
                if (!bundle) return null;
                return (
                  <div key={layerId} className="legend-source-section">
                    <div className="color-legend-title">{bundle.tableDisplayName}</div>
                    {bundle.numericLegend && (
                      <div className="color-legend-gradient">
                        <div className="gradient-bar" />
                        <div className="gradient-labels">
                          <span>{bundle.numericLegend.min.toFixed(1)}</span>
                          <span>{bundle.numericLegend.max.toFixed(1)}</span>
                        </div>
                      </div>
                    )}
                    {bundle.legendEntries.length > 0 && (
                      <div className="color-legend-categorical">
                        {bundle.legendEntries.map(({ value, color }) => (
                          <div key={value} className="legend-item">
                            <span className="legend-swatch" style={{ backgroundColor: color }} />
                            <span className="legend-label">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
              : activeTypesArray.map(sourceId => {
              const legend = legendMap.get(sourceId);
              if (!legend) return null;
              const rScale = radiusScales[sourceId] ?? 1;
              return (
                <div key={sourceId} className="legend-source-section">
                  <div className="color-legend-title">{legend.label}</div>
                  {legend.type === 'numeric' && (
                    <div className="color-legend-gradient">
                      <div className="gradient-bar" />
                      <div className="gradient-labels">
                        <span>{legend.min.toFixed(1)}</span>
                        <span>{legend.max.toFixed(1)}</span>
                      </div>
                      {legend.radiusInfo && (
                        <div className="radius-slider-container">
                          <label className="radius-slider-label">{legend.radiusInfo.label || 'Radius'}</label>
                          <input
                            type="range"
                            className="radius-slider"
                            min="0"
                            max="100"
                            step="1"
                            value={Math.round(rScale * 100)}
                            onChange={(e) => setRadiusScales(prev => ({
                              ...prev, [sourceId]: parseInt(e.target.value, 10) / 100,
                            }))}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {legend.type === 'categorical' && (
                    <div className="color-legend-categorical">
                      {Object.entries(legend.mapping).map(([name, hex]) => (
                        <div key={name} className="legend-item">
                          <span className="legend-swatch" style={{ backgroundColor: hex }} />
                          <span className="legend-label">{name}</span>
                        </div>
                      ))}
                      {legend.radiusInfo && (
                        <div className="radius-slider-container" style={{ marginTop: 6 }}>
                          <label className="radius-slider-label">{legend.radiusInfo.label || 'Radius'}</label>
                          <input
                            type="range"
                            className="radius-slider"
                            min="0"
                            max="100"
                            step="1"
                            value={Math.round(rScale * 100)}
                            onChange={(e) => setRadiusScales(prev => ({
                              ...prev, [sourceId]: parseInt(e.target.value, 10) / 100,
                            }))}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="viewer-3d-legend">
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.75rem', fontWeight: '600', color: '#f9fafb' }}>
            3D Drill Trace
          </h4>
          <div style={{ fontSize: '0.65rem', color: '#9ca3af', lineHeight: '1.4' }}>
            <div>Hole: {selectedHoleId || 'None'}</div>
            <div>
              Data:
              {' '}
              {USE_DRILLZ_GEOMETRY
                ? (geometryScene
                  ? `Drillz BFF (${geometryScene.traces?.length ?? 0} traces)`
                  : 'Drillz BFF (no scene yet)')
                : 'Legacy nearby_traces / downhole_data'}
            </div>
            {geometryRefreshing && <div>Refreshing geometry…</div>}
            {holeCount > 1 && <div>Nearby: {holeCount - 1} holes</div>}
            <div>Points: {selectedTraceInfo?.pointCount || 0}</div>
            {selectedTraceInfo?.depth > 0 && (
              <div>Depth: {selectedTraceInfo.depth.toFixed(1)}m</div>
            )}
            {totalIntervalCount > 0 && (
              <div>Intervals: {totalIntervalCount}</div>
            )}
            {activeTypesArray.length > 0 && (
              <div>
                Sources:
                {' '}
                {USE_DRILLZ_GEOMETRY
                  ? activeTypesArray.map((id) => geometryVisualBundles.get(id)?.tableDisplayName || id).join(', ')
                  : activeTypesArray.map((id) => DOWNHOLE_SOURCES[id].label).join(', ')}
              </div>
            )}
          </div>
          <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.5rem' }}>
            <div>Rotate: Left drag</div>
            <div>Pan: Right drag</div>
            <div>Zoom: Scroll</div>
          </div>
        </div>
      </div>

      <SpatialFilterControls
        radius={spatialRadius}
        onRadiusChange={setSpatialRadius}
        azimuth={azimuth}
        onAzimuthChange={setAzimuth}
        sliceWidth={sliceWidth}
        onSliceWidthChange={setSliceWidth}
        sliceEnabled={sliceEnabled}
        onSliceEnabledChange={setSliceEnabled}
        holeCount={holeCount}
        loading={spatialLoading}
        onSnapView={handleSnapView}
      />
    </div>
  );
});

export default DrillHole3DViewer;
